  import React, { useState, useEffect, useRef } from 'react';
import './profileCreation.scss';
import { Card, Form, Input, Button, Select, Spin, message, Tooltip, Collapse, Modal } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { CloudUploadOutlined, SearchOutlined } from '@ant-design/icons';
import { validationContent } from '../../../constants/Validation';
import { PlaceholderContent } from '../../../constants/Placeholder';
import ImageSelector from '../ImageSelector';
import LocationSelection from '../LocationSelection/LocationSelection';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useMasterData } from '../../../Common/Hooks/useMasterData';
import ProfileDetailsView from './ProfileDetailsView';
import CustomDatePicker from './CustomDatePicker';
import CustomTimePicker from './CustomTimePicker';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../constants/apiEndPoints';
import { createImageUploadFormData } from '../../../utils/imageUtils';
import previewIcon from '../../../assets/images/preview_icon.png';
import Mobile from '../Mobile/Mobile';
import Advertisement from '../Advertisement/Advertisement';
import ListingView from '../../Dashboard/mobile/ListingView/ListingView';
import ProfileDetails from '../../Dashboard/mobile/ListingView/ProfileDetails';
import { useDispatch, useSelector } from 'react-redux';
import { getMobileData } from '../../../slices/mobile-view/thunk';
import calendarIcon from '../../../assets/images/calendar_icon.png';
import dropdownIcon from '../../../assets/images/bottom_arrow.png';

// Configure dayjs plugins
dayjs.extend(customParseFormat);

const { TextArea } = Input;
const { Option } = Select;

// Validation and placeholder content
let content = validationContent?.PROFILE_CREATION;
let placeHolderContent = PlaceholderContent?.PROFILE_CREATION;

const ProfileCreation = () => {
  document.title = "Profile Creation ";   
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();

  const [selectedImages, setSelectedImages] = useState([]);
  const [imageError, setImageError] = useState(false);
  const [imageValidationError, setImageValidationError] = useState(false);
  const [interests, setInterests] = useState([]);
  const [selectedInterestsData, setSelectedInterestsData] = useState([]); // Store complete interest objects
  const [selectedLanguagesData, setSelectedLanguagesData] = useState([]); // Store complete language objects
  const [showDetailsView, setShowDetailsView] = useState(false);
  const [submittedProfileData, setSubmittedProfileData] = useState(null);
  const [submissionLoading, setSubmissionLoading] = useState(false);
  const [imageUploadLoading, setImageUploadLoading] = useState(false);
  const [uploadedImageUrls, setUploadedImageUrls] = useState([]);
  const [showUploadButton, setShowUploadButton] = useState(false);
  const [imagesUploaded, setImagesUploaded] = useState(false);

  // Horoscope upload states
  const [selectedHoroscopeImages, setSelectedHoroscopeImages] = useState([]);
  const [horoscopeImageError, setHoroscopeImageError] = useState(false);
  const [horoscopeUploadLoading, setHoroscopeUploadLoading] = useState(false);
  const [uploadedHoroscopeUrls, setUploadedHoroscopeUrls] = useState([]);
  const [showHoroscopeUploadButton, setShowHoroscopeUploadButton] = useState(false);
  const [horoscopeImagesUploaded, setHoroscopeImagesUploaded] = useState(false);
  // Lock caste dropdown while refreshing due to religion change
  const [isCasteRefreshing, setIsCasteRefreshing] = useState(false);
  const casteLoadingReligionRef = useRef(null);
  const [currentProfileId, setCurrentProfileId] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [bioRegenerateLoading, setBioRegenerateLoading] = useState(false);
  const [isPrefilling, setIsPrefilling] = useState(false);

  // Preview modal states
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewErrorModalOpen, setPreviewErrorModalOpen] = useState(false);
  const [activePreviewTab, setActivePreviewTab] = useState('listing');
  const [mockListingData, setMockListingDataState] = useState([]);

  // Location search states
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedBirthLocation, setSelectedBirthLocation] = useState(null);

  // Marital status dropdown state
  const [childrenFieldActive, setChildrenFieldActive] = useState(false);
  const [childrenDropdownOpen, setChildrenDropdownOpen] = useState(false);
  const [maritalCustomDropdownOpen, setMaritalCustomDropdownOpen] = useState(false);
  const [maritalStatusSearchTerm, setMaritalStatusSearchTerm] = useState('');

  // Ref to store pending caste data for prefill
  const pendingCasteDataRef = useRef(null);

  // Ref to track prefilling timeout
  const prefillingTimeoutRef = useRef(null);

  // Ref for children dropdown to handle click outside
  const childrenDropdownRef = useRef(null);

  // Ref for marital status custom dropdown to handle click outside
  const maritalCustomDropdownRef = useRef(null);
  // Track if a selection was made while the marital dropdown was open
  const maritalSelectionMadeRef = useRef(false);

  // Redux selector for mobile preview data
  const mobListingData = useSelector((state) => state.MobileViewListing);

  // Form data state
  const [formData, setFormData] = useState({
    fullName: '',
    motherTongue: undefined, // Use undefined for select fields to show placeholders
    dob: '',
    gender: undefined, // Use undefined for select fields to show placeholders
    maritalStatus: undefined, // Use undefined for select fields to show placeholders
    religion: undefined, // Use undefined for select fields to show placeholders
    caste: undefined, // Use undefined for select fields to show placeholders
    height: undefined, // Use undefined for select fields to show placeholders
    birthStar: undefined, // Use undefined for select fields to show placeholders
    job: undefined, // Use undefined for select fields to show placeholders
    education: undefined, // Use undefined for select fields to show placeholders
    isDisabled: undefined, // Use undefined for select fields to show placeholders
    location: '',
    bio: '',
    lookingFor: '',
    // Additional Details fields
    weight: undefined, // Use undefined for select fields to show placeholders
    interests: undefined, // Use undefined for select fields to show placeholders
    birthTime: '',
    familyStatus: undefined, // Use undefined for select fields to show placeholders
    dosham: undefined, // Use undefined for select fields to show placeholders
    birthPlace: '',
    knownLanguages: [],
    fatherName: '',
    fatherOccupation: '',
    familyName: '',
    motherName: '',
    motherOccupation: '',
    brothersCount: 0,
    sistersCount: 0,
    familyLocation: '',
    familyDescription: '',
    drinking: undefined, // Use undefined for select fields to show placeholders
    ideologies: undefined, // Use undefined for select fields to show placeholders
    food: undefined, // Use undefined for select fields to show placeholders
    smoking: undefined, // Use undefined for select fields to show placeholders
    // Conditional field based on marital status
    noOfChildren: 0
  });

  // Field limits
  const [fieldLimits] = useState({
    fullName: 50,
    bio: 500
  });

  // Field lengths for character count
  const [fieldLengths, setFieldLengths] = useState({
    fullName: 0,
    bio: 0
  });

  useEffect(()=>{
  },[formData])

  // Master data hooks with pagination support - only load first page initially
  const {
    data: languages,
    loading: languagesLoading,
    loadingMore: languagesLoadingMore,
    hasMore: languagesHasMore,
    handleScroll: handleLanguagesScroll,
    loadMore: loadMoreLanguages,
    error: languagesError,
    search: searchLanguages
  } = useMasterData('languages', { enableInfiniteScroll: true });

  const {
    data: maritalStatuses,
    loading: maritalStatusesLoading,
    loadingMore: maritalStatusesLoadingMore,
    hasMore: maritalStatusesHasMore,
    handleScroll: handleMaritalStatusesScroll,
    loadMore: loadMoreMaritalStatuses,
    search: searchMaritalStatuses,
    totalCount: maritalStatusesTotalCount
  } = useMasterData('maritalStatuses', { enableInfiniteScroll: true });

  const {
    data: religions,
    loading: religionsLoading,
    loadingMore: religionsLoadingMore,
    hasMore: religionsHasMore,
    handleScroll: handleReligionsScroll,
    loadMore: loadMoreReligions,
    error: religionsError,
    search: searchReligions,
    totalCount: religionsTotalCount
  } = useMasterData('religions', { enableInfiniteScroll: true });

  // Debug logging for master data
  React.useEffect(() => {
    // Languages debug info removed
  }, [languages, languagesLoading, languagesError, loadMoreLanguages]);

  React.useEffect(() => {
    // Religions debug info removed
  }, [religions, religionsLoading, religionsError, loadMoreReligions]);



  // Religion to division code mapping (using both codes and labels for compatibility)
  const religionToDivisionCode = React.useMemo(() => ({
    // Label-based mapping
    'Hindu': 'HIN',
    'Muslim': 'MUS',
    'Christian': 'CHR',
    'Sikh': 'SIK',
    'Buddhist': 'BUD',
    'Jain': 'JAI',
    'Parsi': 'PAR',
    'Jew': 'JEW',
    'Atheist': 'ATH',
    'Inter-Religion': 'INT',
    'Other': 'OTH',
    // Code-based mapping (common codes)
    'HIN': 'HIN',
    'MUS': 'MUS',
    'CHR': 'CHR',
    'SIK': 'SIK',
    'BUD': 'BUD',
    'JAI': 'JAI',
    'PAR': 'PAR',
    'JEW': 'JEW',
    'ATH': 'ATH',
    'INT': 'INT',
    'OTH': 'OTH',
    // Alternative code mappings
    '1': 'HIN',   // Hindu
    '2': 'MUS',   // Muslim
    '3': 'CHR',   // Christian
    '4': 'SIK',   // Sikh
    '5': 'BUD',   // Buddhist
    '6': 'JAI',   // Jain
    '7': 'OTH'    // Other
  }), []);

  // Memoize the params to prevent infinite re-renders
  const casteParams = React.useMemo(() => {
    const divisionCode = formData.religion ? religionToDivisionCode[formData.religion] || null : null;
    return {
      divisionCode
    };
  }, [formData.religion, religionToDivisionCode]);

  const {
    data: castes,
    loading: castesLoading,
    loadingMore: castesLoadingMore,
    hasMore: castesHasMore,
    handleScroll: handleCastesScroll,
    loadMore: loadMoreCastes,
    search: searchCastes,
    totalCount: castesTotalCount,
    refresh: refreshCastes,
    clearCache: clearCastesCache
  } = useMasterData('castes', {
    enableInfiniteScroll: true,
    params: casteParams
  });

  const {
    data: heights,
    loading: heightsLoading,
    loadingMore: heightsLoadingMore,
    hasMore: heightsHasMore,
    handleScroll: handleHeightsScroll,
    loadMore: loadMoreHeights,
    search: searchHeights,
    totalCount: heightsTotalCount
  } = useMasterData('heights', { enableInfiniteScroll: true });

  React.useEffect(() => {
    // Heights debug info removed
  }, [heights, heightsLoading, loadMoreHeights]);

  // Additional debugging for heights data changes
  React.useEffect(() => {
    // Heights data change debug removed
  }, [heights, heightsLoading]);

  const {
    data: birthStars,
    loading: birthStarsLoading,
    loadingMore: birthStarsLoadingMore,
    hasMore: birthStarsHasMore,
    handleScroll: handleBirthStarsScroll,
    loadMore: loadMoreBirthStars,
    search: searchBirthStars
  } = useMasterData('birthStars', { enableInfiniteScroll: true });

  const {
    data: jobs,
    loading: jobsLoading,
    loadingMore: jobsLoadingMore,
    hasMore: jobsHasMore,
    handleScroll: handleJobsScroll,
    loadMore: loadMoreJobs,
    search: searchJobs,
    totalCount: jobsTotalCount
  } = useMasterData('jobs', { enableInfiniteScroll: true });

  const {
    data: educations,
    loading: educationsLoading,
    loadingMore: educationsLoadingMore,
    hasMore: educationsHasMore,
    handleScroll: handleEducationsScroll,
    loadMore: loadMoreEducations,
    search: searchEducations
  } = useMasterData('educations', { enableInfiniteScroll: true });

  const {
    data: interestsList,
    loading: interestsLoading,
    loadingMore: interestsLoadingMore,
    hasMore: interestsHasMore,
    handleScroll: handleInterestsScroll,
    loadMore: loadMoreInterests,
    search: searchInterests
  } = useMasterData('interests', { enableInfiniteScroll: true });

  const {
    data: foodOptions,
    loading: foodLoading,
    loadingMore: foodLoadingMore,
    hasMore: foodHasMore,
    handleScroll: handleFoodScroll,
    loadMore: loadMoreFood,
    search: searchFood
  } = useMasterData('food', { enableInfiniteScroll: true });

  const {
    data: drinkingOptions,
    loading: drinkingLoading,
    loadingMore: drinkingLoadingMore,
    hasMore: drinkingHasMore,
    handleScroll: handleDrinkingScroll,
    loadMore: loadMoreDrinking,
    search: searchDrinking
  } = useMasterData('drinking', { enableInfiniteScroll: true });

  const {
    data: smokingOptions,
    loading: smokingLoading,
    loadingMore: smokingLoadingMore,
    hasMore: smokingHasMore,
    handleScroll: handleSmokingScroll,
    loadMore: loadMoreSmoking,
    search: searchSmoking
  } = useMasterData('smoking', { enableInfiniteScroll: true });

  const {
    data: ideologyOptions,
    loading: ideologyLoading,
    loadingMore: ideologyLoadingMore,
    hasMore: ideologyHasMore,
    handleScroll: handleIdeologyScroll,
    loadMore: loadMoreIdeology,
    search: searchIdeologies
  } = useMasterData('ideologies', { enableInfiniteScroll: true });

  const {
    data: weightOptions,
    loading: weightLoading,
    loadingMore: weightLoadingMore,
    hasMore: weightHasMore,
    handleScroll: handleWeightScroll,
    loadMore: loadMoreWeight,
    search: searchWeights
  } = useMasterData('weights', { enableInfiniteScroll: true });

  const {
    data: manglikOptions,
    loading: manglikLoading,
    loadingMore: manglikLoadingMore,
    hasMore: manglikHasMore,
    handleScroll: handleManglikScroll,
    loadMore: loadMoreManglik,
    search: searchManglikLists
  } = useMasterData('manglikLists', { enableInfiniteScroll: true });

  const {
    data: familyTypes,
    loading: familyTypesLoading,
    loadingMore: familyTypesLoadingMore,
    hasMore: familyTypesHasMore,
    handleScroll: handleFamilyTypesScroll,
    loadMore: loadMoreFamilyTypes,
    search: searchFamilyTypes
  } = useMasterData('familyTypes', { enableInfiniteScroll: true });

  // Utility function to format time value for form storage
  const formatTimeForStorage = React.useCallback((timeValue) => {
    if (!timeValue) return '';

    try {
      // Handle different time formats
      let timeString = '';

      if (typeof timeValue === 'string') {
        // If it's already a formatted time string like "09:30 AM"
        if (timeValue.includes(':') && (timeValue.includes('AM') || timeValue.includes('PM'))) {
          return timeValue;
        }

        // If it's an ISO string (e.g., "2025-10-10T01:00:00Z"), extract time part WITHOUT timezone conversion
        if (timeValue.includes('T')) {
          const date = new Date(timeValue);
          // Use UTC methods to avoid timezone conversion - keep the exact time as stored
          const hours = date.getUTCHours();
          const minutes = date.getUTCMinutes();
          const ampm = hours >= 12 ? 'PM' : 'AM';
          const displayHours = hours % 12 || 12;
          timeString = `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
        }
      } else if (timeValue instanceof Date) {
        // If it's a Date object, use UTC methods
        const hours = timeValue.getUTCHours();
        const minutes = timeValue.getUTCMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        timeString = `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
      }

      return timeString;
    } catch (error) {
      return '';
    }
  }, []);

  // Transform API response data to display format
  const transformApiDataToDisplayData = React.useCallback((apiResponse) => {

    // Handle both old and new API response structures
    const apiData = apiResponse.data || apiResponse;

    const displayData = {
      id: apiData.id || apiData._id || 'profile_' + Date.now(),
      fullName: apiData.name || '',
      motherTongue: apiData.mother_tongue?.label || '',
      mother_tongue: apiData.mother_tongue || null,
      dob: apiData.dob ? new Date(apiData.dob).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      }).replace(/\//g, '-') : '',
      gender: apiData.gender?.label || '',
      maritalStatus: apiData.marital_status?.label || '',
      religion: apiData.religion?.label || '',
      caste: apiData.caste?.label || '',
      height: apiData.height?.label || '',
      birthStar: apiData.birth_star?.label || '',
      job: apiData.job?.label || '',
      education: apiData.education?.label || '',
      isDisabled: apiData.physically_disabled ? 'Yes' : 'No',
      location: apiData.city?.city?.label || apiData.location || '',
      bio: apiData.bio || '',
      state: apiData.state?.label || '',
      city: apiData.city?.label || apiData.city?.city?.label || '',
      weight: apiData.weight?.label || '',
      interests: apiData.interests?.filter(i => i.label && i.code).map(interest => ({
        label: interest.label,
        code: interest.code
      })) || [],

      // Transform images - handle both original API structure and test data structure
      images: apiData.profile_images?.map((img, index) => ({
        id: img.id || `img_${index}`,
        fileName: `image_${index}.jpg`,
        originalName: `image_${index}.jpg`,
        url: img.original || img.url,
        s3Url: img.original || img.s3Url,
        thumbnail: img.thumbnail || img.original,
        contentType: 'image/jpeg',
        fileSize: 0,
        uploadType: 'jpeg',
        status: 'uploaded',
        isProfile: img.is_profile || false,
        verificationStatus: img.verification_status || 0,
        createdAt: new Date().toISOString()
      })) || [],

      // Location data - handle both old and new structures
      locationData: apiData.city ? {
        id: apiData.city.id || apiData.city.city?.code || '',
        displayName: apiData.city.display_name || apiData.city.city?.label || '',
        displayPlace: apiData.city.display_place || apiData.city.city?.label || '',
        displayAddress: apiData.city.display_address || `${apiData.city.state?.label || ''}, ${apiData.city.country?.label || ''}`,
        name: apiData.city.name || apiData.city.city?.label || '',
        country: apiData.city.country || { code: 'IN', label: 'India' },
        state: apiData.city.state || { code: '', label: '' },
        city: apiData.city.city || { district: '', label: '', code: '' },
        geojson: apiData.city.geojson || { type: 'Point', coordinates: [0, 0] },
        lat: apiData.city.lat || '',
        lon: apiData.city.lon || '',
        county: apiData.city.county || '',
        zipcode: apiData.city.zipcode || ''
      } : null,

      // Family location data
      familyLocationData: apiData.family_location ? {
        id: apiData.family_location.id || '',
        displayName: apiData.family_location.display_name || '',
        displayPlace: apiData.family_location.display_place || '',
        displayAddress: apiData.family_location.display_address || '',
        name: apiData.family_location.name || '',
        country: apiData.family_location.country || { code: '', label: '' },
        state: apiData.family_location.state || { code: '', label: '' },
        city: apiData.family_location.city || { district: '', label: '', code: '' },
        geojson: apiData.family_location.geojson || { type: '', coordinates: null },
        lat: apiData.family_location.lat || '',
        lon: apiData.family_location.lon || '',
        county: apiData.family_location.county || '',
        zipcode: apiData.family_location.zipcode || ''
      } : null,

      // Birth location data
      birthLocationData: apiData.birth_location ? {
        id: apiData.birth_location.id || '',
        displayName: apiData.birth_location.display_name || '',
        displayPlace: apiData.birth_location.display_place || '',
        displayAddress: apiData.birth_location.display_address || '',
        name: apiData.birth_location.name || '',
        country: apiData.birth_location.country || { code: '', label: '' },
        state: apiData.birth_location.state || { code: '', label: '' },
        city: apiData.birth_location.city || { district: '', label: '', code: '' },
        geojson: apiData.birth_location.geojson || { type: '', coordinates: null },
        lat: apiData.birth_location.lat || '',
        lon: apiData.birth_location.lon || '',
        county: apiData.birth_location.county || '',
        zipcode: apiData.birth_location.zipcode || ''
      } : null,

      // Transform habits - handle the new structure with code-based lookup
      food: apiData.habit?.find(h => h.code === "0")?.sub_division?.[0]?.label || '',
      drinking: apiData.habit?.find(h => h.code === "1")?.sub_division?.[0]?.label || '',
      smoking: apiData.habit?.find(h => h.code === "2")?.sub_division?.[0]?.label || '',
      ideologies: apiData.habit?.find(h => h.code === "3")?.sub_division?.[0]?.label || '',
      habit: apiData.habit || [],

      // Add additional fields for better display
      age: apiData.age || (apiData.dob ? Math.floor((new Date() - new Date(apiData.dob)) / (365.25 * 24 * 60 * 60 * 1000)) : ''),
      knownLanguages: apiData.languages_known?.filter(lang => lang.label && lang.code).map(lang => {
        // Find the language by label/code to get the ID
        const found = languages.find(language => language.label === lang.label || language.code === lang.code);
        return found ? found.id : lang.label; // Fallback to label if ID not found
      }) || [],
      languages_known: apiData.languages_known?.filter(lang => lang.label && lang.code) || [],

      // Additional profile fields
      birthTime: (apiData.birth_date_time && apiData.birth_date_time !== "0001-01-01T00:00:00Z") ? formatTimeForStorage(apiData.birth_date_time) :
        (apiData.birth_time && apiData.birth_time !== "0001-01-01T00:00:00Z") ? formatTimeForStorage(apiData.birth_time) : '',
      familyStatus: apiData.family?.family_type?.label || '',
      dosham: apiData.manglik?.label || '',
      manglik: apiData.manglik || null, // Preserve full manglik object for ProfileDetailsCards
      birthPlace: apiData.birth_place || '',
      fatherName: apiData.family?.father_name || '',
      fatherOccupation: apiData.family?.father_job || '',
      familyName: apiData.family?.family_name || '',
      motherName: apiData.family?.mother_name || '',
      motherOccupation: apiData.family?.mother_job || '',
      brothersCount: apiData.family?.brothers_count || 0,
      sistersCount: apiData.family?.sisters_count || 0,
      siblings: apiData.family?.siblings || [],
      familyLocation: apiData.family?.family_location || '',
      familyDescription: apiData.family?.family_bio || '',
      // Add children count field - read from family object
      noOfChildren: apiData.family?.no_of_children || 0,
      
      // Preserve family object for ProfileDetailsCards
      family: apiData.family || null,

      // Additional profile fields
      phone: apiData.phone || '',
      countryCode: apiData.country_code || '',
      bureauId: apiData.bureau_id || '',
      astroSign: apiData.astro_sign || '',
      profileVerified: apiData.profile_verified || false,
      profileVerifiedAdmin: apiData.profile_verified_admin || false,
      profileCompletion: apiData.profile_completion || 0,
      profileCompleted: apiData.profile_completed || false,
      isEmailVerified: apiData.is_email_verified || false,
      isBlocked: apiData.is_blocked || false,
      isRestricted: apiData.is_restricted || false,
      reviewStatus: apiData.review_status || '',
      lumiId: apiData.lumi_id || '',
      referralCode: apiData.referral_code || '',
      views: apiData.views || 0,
      clicks: apiData.clicks || 0,
      contactsAccessed: apiData.contacts_accessed || 0,
      enquiries: apiData.enquiries || 0,

      // Social media fields
      email: apiData.email || '',
      instagram: apiData.instagram || '',
      facebook: apiData.facebook || '',

      // Horoscope images
      horoscopeImages: apiData.horoscope_images || [],

      // Store original API data for reference
      originalData: apiData,
      originalResponse: apiResponse,

      createdAt: apiData.created_at || apiData.createdAt || new Date().toISOString(),
      updatedAt: apiData.updated_at || apiData.updatedAt || new Date().toISOString(),
      lastLogin: apiData.last_login || new Date().toISOString(),
      status: apiData.status || apiData.profile_status || 'pending'
    };

    return displayData;
  }, [formatTimeForStorage]);



  // Helper function to calculate siblings count from API siblings array
  const calculateSiblingsCount = React.useCallback((siblingsData) => {
    if (!Array.isArray(siblingsData) || siblingsData.length === 0) {
      return { brothersCount: 0, sistersCount: 0 };
    }

    let brothersCount = 0;
    let sistersCount = 0;

    siblingsData.forEach(sibling => {
      // Handle Key-Value array format: [{Key: "is_male", Value: true}, ...]
      if (Array.isArray(sibling)) {
        const siblingObj = sibling.reduce((acc, item) => {
          if (item && typeof item === 'object' && 'Key' in item) {
            acc[item.Key] = item.Value;
          }
          return acc;
        }, {});

        if (siblingObj.is_male === true || siblingObj.is_male === 'true' || siblingObj.is_male === 1) {
          brothersCount++;
        } else if (siblingObj.is_male === false || siblingObj.is_male === 'false' || siblingObj.is_male === 0) {
          sistersCount++;
        }
      }
      // Handle direct object format: {is_male: true, ...}
      else if (sibling && typeof sibling === 'object') {
        if (sibling.is_male === true || sibling.is_male === 'true' || sibling.is_male === 1) {
          brothersCount++;
        } else if (sibling.is_male === false || sibling.is_male === 'false' || sibling.is_male === 0) {
          sistersCount++;
        }
      }
    });

    return { brothersCount, sistersCount };
  }, []);

  // Prefill form with profile data for editing
  const prefillFormWithProfileData = React.useCallback((apiData) => {
    // Set prefilling flag to prevent clearing caste during religion change
    setIsPrefilling(true);

    // Clear any pending caste data from previous edit
    pendingCasteDataRef.current = null;

    // Map gender code to label for the dropdown
    const getGenderLabel = (genderData) => {
      if (!genderData) return '';
      // If we have a label, check if it's "Male" or "Female" and return it
      if (genderData.label === 'Male' || genderData.label === 'Female') {
        return genderData.label;
      }
      // Otherwise map code to label
      const code = genderData.code || '';
      if (code === '0' || code === 0) return 'Male';
      if (code === '1' || code === 1) return 'Female';
      return '';
    };

    const formValues = {
      fullName: apiData.name || '',
      motherTongue: apiData.mother_tongue?.code || '',
      dob: apiData.dob ? new Date(apiData.dob).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      }).replace(/\//g, '-') : '',
      gender: getGenderLabel(apiData.gender),
      maritalStatus: apiData.marital_status?.code || '',
      religion: apiData.religion?.code || '',
      caste: apiData.caste?.label || '', // Will be set after castes load
      height: apiData.height?.label || '', // Store code for Select component value matching
      birthStar: apiData.birth_star?.label || '',
      job: apiData.job?.label || '',
      education: apiData.education?.label || '',
      isDisabled: apiData.physically_disabled ? 'Yes' : 'No',
      location: apiData.city?.city?.label || apiData.location || '',
      bio: apiData.bio || '',
      lookingFor: apiData.looking_for || apiData.lookingFor || '',
      weight: apiData.weight?.label || '', // Store code for Select component value matching
      interests: apiData.interests?.filter(i => i.label && i.code).map(interest => ({
        label: interest.label,
        code: interest.code
      })) || [],

      // Transform time data safely - check both birth_date_time and birth_time fields
      birthTime: (apiData.birth_date_time && apiData.birth_date_time !== "0001-01-01T00:00:00Z") ? formatTimeForStorage(apiData.birth_date_time) :
        (apiData.birth_time && apiData.birth_time !== "0001-01-01T00:00:00Z") ? formatTimeForStorage(apiData.birth_time) : '',

      // Transform habits back to form values - use labels for form display
      food: apiData.habit?.find(h => h.code === "0")?.sub_division?.[0]?.label || '',
      drinking: apiData.habit?.find(h => h.code === "1")?.sub_division?.[0]?.label || '',
      smoking: apiData.habit?.find(h => h.code === "2")?.sub_division?.[0]?.label || '',
      ideologies: apiData.habit?.find(h => h.code === "3")?.sub_division?.[0]?.label || '',

      // Family fields from the family object
      familyStatus: apiData.family?.family_type?.label || '',
      dosham: apiData.manglik?.label || '',
      birthPlace: apiData.birth_place || '',
      knownLanguages: apiData.languages_known?.filter(lang => lang.label && lang.code).map(lang => {
        // Find the language by label/code to get the ID
        const found = languages.find(language => language.label === lang.label || language.code === lang.code);
        return found ? found.id : lang.label; // Fallback to label if ID not found
      }) || [],
      fatherName: apiData.family?.father_name || '',
      fatherOccupation: apiData.family?.father_job || '',
      familyName: apiData.family?.family_name || '',
      motherName: apiData.family?.mother_name || '',
      motherOccupation: apiData.family?.mother_job || '',
      // Calculate siblings count from siblings array or use stored counts
      ...(() => {
        const siblingsArray = apiData.family?.siblings || [];
        if (siblingsArray.length > 0) {
          return calculateSiblingsCount(siblingsArray);
        }
        // Fallback to stored counts if siblings array is empty
        return {
          brothersCount: apiData.family?.brothers_count || 0,
          sistersCount: apiData.family?.sisters_count || 0
        };
      })(),
      siblings: apiData.family?.siblings || [],
      // Family location is always a simple string (e.g., "Kochi, Kerala"), not an object
      // Handle both string and object formats from API
      familyLocation: (() => {
        const familyLoc = apiData.family?.family_location || apiData.family_location;
        if (!familyLoc) return '';
        // If it's already a string, return it
        if (typeof familyLoc === 'string') return familyLoc;
        // If it's an object, extract the display value
        if (typeof familyLoc === 'object') {
          return familyLoc.display_place || 
                 familyLoc.displayPlace || 
                 familyLoc.display_name || 
                 familyLoc.displayName || 
                 familyLoc.name || 
                 familyLoc.label || 
                 '';
        }
        return String(familyLoc);
      })(),
      familyDescription: apiData.family?.family_bio || '',
      // Add children count field - read from family object
      noOfChildren: apiData.family?.no_of_children || 0
    };

    // Set form values first (without caste)
    // Filter out undefined values to allow placeholders to show, but preserve empty strings and other values
    const filteredFormValues = Object.fromEntries(
      Object.entries(formValues).filter(([key, value]) => value !== undefined)
    );
    form.setFieldsValue(filteredFormValues);
    setFormData(formValues);

    // Update field lengths for character count tracking
    setFieldLengths({
      fullName: formValues.fullName?.length || 0,
      bio: formValues.bio?.length || 0
    });

   

    // Store the caste data for later use when caste options load
    // Also store the religion code to ensure we're matching against the right caste list
    if (apiData.caste?.code || apiData.caste?.label) {
      pendingCasteDataRef.current = {
        id: apiData.caste.id || apiData.caste.code,
        code: apiData.caste.code,
        label: apiData.caste.label,
        religionCode: apiData.religion?.code || formValues.religion // Store religion code for validation
      };
    }

    // Set interests from the interests array
    if (apiData.interests && Array.isArray(apiData.interests)) {
      const interestIds = apiData.interests.filter(i => i.label && i.code).map(interest => {
        // Find the interest by label/code to get the ID
        const found = interestsList.find(int => int.label === interest.label || int.code === interest.code);
        return found ? found.id : interest.label; // Fallback to label if ID not found
      });
      setInterests(interestIds);
      
      // Also store the complete interest objects
      const interestObjects = apiData.interests.filter(i => i.label && i.code).map(interest => ({
        code: interest.code,
        label: interest.label
      }));
      setSelectedInterestsData(interestObjects);
    }

    // Set known languages objects from the languages_known array
    if (apiData.languages_known && Array.isArray(apiData.languages_known)) {
      const languageObjects = apiData.languages_known.filter(lang => lang.label && lang.code).map(lang => ({
        code: lang.code,
        label: lang.label
      }));
      setSelectedLanguagesData(languageObjects);
    }

    // Set location data with improved handling
    if (apiData.city) {
      const locationData = {
        id: apiData.city.id || apiData.city.city?.code || '',
        lat: apiData.city.lat || '',
        lon: apiData.city.lon || '',
        displayName: apiData.city.display_name || apiData.city.city?.label || '',
        displayPlace: apiData.city.display_place || apiData.city.city?.label || '',
        displayAddress: apiData.city.display_address || `${apiData.city.state?.label || ''}, ${apiData.city.country?.label || ''}`,
        name: apiData.city.name || apiData.city.city?.label || '',
        county: apiData.city.county || '',
        zipcode: apiData.city.zipcode || '',
        country: apiData.city.country || { code: 'IN', label: 'India' },
        state: apiData.city.state || { code: '', label: '' },
        city: apiData.city.city || { district: '', label: '', code: '' },
        geojson: apiData.city.geojson || { type: 'Point', coordinates: [0, 0] },
        iteration: apiData.city.iteration || 0
      };
      
      setSelectedLocation(locationData);
      
      // Update the location form field with display place
      form.setFieldValue('location', locationData.displayPlace || locationData.name);
    }

    // Set birth location data if available
    if (apiData.birth_location) {
      const birthLocationData = {
        id: apiData.birth_location.id || '',
        lat: apiData.birth_location.lat || '',
        lon: apiData.birth_location.lon || '',
        displayName: apiData.birth_location.display_name || '',
        displayPlace: apiData.birth_location.display_place || '',
        displayAddress: apiData.birth_location.display_address || '',
        name: apiData.birth_location.name || '',
        county: apiData.birth_location.county || '',
        zipcode: apiData.birth_location.zipcode || '',
        country: apiData.birth_location.country || { code: '', label: '' },
        state: apiData.birth_location.state || { code: '', label: '' },
        city: apiData.birth_location.city || { district: '', label: '', code: '' },
        geojson: apiData.birth_location.geojson || { type: '', coordinates: null },
        iteration: 0
      };
      setSelectedBirthLocation(birthLocationData);
      
      // Update the birth place form field with display place
      form.setFieldValue('birthPlace', birthLocationData.displayPlace || birthLocationData.name);
    }

    // Set images if available
    if (apiData.profile_images && Array.isArray(apiData.profile_images)) {
      const images = apiData.profile_images.map((img, index) => ({
        uid: `existing_img_${index}`,
        name: `image_${index}.jpg`,
        url: img.original,
        s3Url: img.original,
        file: null,
        status: 'done'
      }));

      setSelectedImages(images);
      setUploadedImageUrls(apiData.profile_images.map((img, index) => ({
        id: img.id || `existing_${index}`,
        fileName: `image_${index}.jpg`,
        originalName: `image_${index}.jpg`,
        s3Url: img.original,
        contentType: 'image/jpeg',
        fileSize: 0,
        uploadType: 'jpeg',
        status: 'uploaded',
        createdAt: new Date().toISOString()
      })));
      setImagesUploaded(true);
      setShowUploadButton(false); // Don't show upload button for existing images
    }

    // Set horoscope images if available
    if (apiData.horoscope_images && Array.isArray(apiData.horoscope_images)) {
      const horoscopeImages = apiData.horoscope_images.map((img, index) => ({
        uid: `existing_horoscope_${index}`,
        name: `horoscope_${index}.jpg`,
        url: img.original,
        s3Url: img.original,
        file: null,
        status: 'done'
      }));

      setSelectedHoroscopeImages(horoscopeImages);
      setUploadedHoroscopeUrls(apiData.horoscope_images.map((img, index) => ({
        id: img.id || `existing_horoscope_${index}`,
        fileName: `horoscope_${index}.jpg`,
        originalName: `horoscope_${index}.jpg`,
        s3Url: img.original,
        contentType: 'image/jpeg',
        fileSize: 0,
        uploadType: 'jpeg',
        status: 'uploaded',
        createdAt: new Date().toISOString()
      })));
      setHoroscopeImagesUploaded(true);
      setShowHoroscopeUploadButton(false); // Don't show upload button for existing horoscope images
    }

    // Safety timeout to clear prefilling flag after 10 seconds
    // Increased timeout to allow for caste data to load after religion is set
    // Clear any existing timeout first
    if (prefillingTimeoutRef.current) {
      clearTimeout(prefillingTimeoutRef.current);
    }
    
    prefillingTimeoutRef.current = setTimeout(() => {
      // Only clear prefilling flag if caste hasn't been set yet
      // If caste was successfully prefilled, it will clear the flag itself
      if (!formData.caste && pendingCasteDataRef.current) {
        // Caste prefilling failed or timed out
        // Clear the prefilling flag but keep pending data for retry
        setIsPrefilling(false);
      } else if (!pendingCasteDataRef.current) {
        // Caste was successfully prefilled, just clear the flag
        setIsPrefilling(false);
      }
      prefillingTimeoutRef.current = null;
    }, 10000); // Increased from 3 seconds to 10 seconds

  }, [form, formatTimeForStorage, calculateSiblingsCount]);


  // Fetch profile details for viewing
  const fetchProfileDetails = React.useCallback(async (profileId) => {
    if (!profileId) return;

    setProfileLoading(true);
    try {
      const response = await axios.get(API_ENDPOINTS.PROFILE.GET(profileId));

      if (response.success && response.data) {
        const profileData = transformApiDataToDisplayData(response.data);
        setSubmittedProfileData(profileData);
        setShowDetailsView(true);
        
        // Update URL to reflect details view state if not already on profile-details route
        if (!location.pathname.includes('/profile-details/')) {
          navigate(`/profile-details/${profileId}`, { replace: true });
        }
      } else {
        throw new Error(response.message || 'Failed to fetch profile details');
      }
    } catch (error) {
      message.error('Failed to load profile details. Please try again.');
      navigate('/profile-creation');
    } finally {
      setProfileLoading(false);
    }
  }, [transformApiDataToDisplayData, navigate, location.pathname]);

  // Fetch profile data for editing
  const fetchProfileForEdit = React.useCallback(async (profileId) => {
    if (!profileId) return;

    setProfileLoading(true);
    try {
      const response = await axios.get(API_ENDPOINTS.PROFILE.GET(profileId));

      if (response.success && response.data) {
        prefillFormWithProfileData(response.data);
      } else {
        throw new Error(response.message || 'Failed to fetch profile data');
      }
    } catch (error) {
      message.error('Failed to load profile data. Please try again.');
      navigate('/profile-creation');
    } finally {
      setProfileLoading(false);
    }
  }, [prefillFormWithProfileData, navigate]);

  // Helper function to calculate max allowed DOB based on gender
  const getMaxAllowedDOB = React.useCallback((gender) => {
    const today = new Date();
    const minAge = gender === 'Male' ? 21 : gender === 'Female' ? 18 : 18;
    
    // Calculate the maximum date (minimum age years ago from today)
    const maxDate = new Date(
      today.getFullYear() - minAge,
      today.getMonth(),
      today.getDate()
    );
    
    return maxDate;
  }, []);

  // Handle form field changes
  const handleFieldChange = React.useCallback((fieldName, value) => {
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [fieldName]: value
      };

      // When gender changes, validate and potentially reset DOB
      if (fieldName === 'gender' && prev.dob) {
        const maxAllowedDOB = getMaxAllowedDOB(value);
        const currentDOB = new Date(prev.dob);
        
        // If current DOB is invalid for the new gender, clear it
        if (currentDOB > maxAllowedDOB) {
          newFormData.dob = '';
          form.setFieldValue('dob', '');
          message.warning(`Date of birth has been cleared. ${value === 'Male' ? 'Males' : 'Females'} must be at least ${value === 'Male' ? '21' : '18'} years old.`);
        }
      }

      return newFormData;
    });

    // Update field lengths for character count
    if (fieldName === 'fullName' || fieldName === 'bio') {
      setFieldLengths(prev => ({
        ...prev,
        [fieldName]: value?.length || 0
      }));
    }
    // Note: birthPlace is handled separately in handleBirthPlaceSelection
    // Don't set selectedBirthLocation here to avoid conflicts
  }, [form, getMaxAllowedDOB]);

  // Memoized location selection handler for main location
  const handleMainLocationSelection = React.useCallback((location) => {
    if (location) {
      handleFieldChange('location', location.display_place || location.name);
      setSelectedLocation(location);
      form.setFieldValue('location', location.display_place || location.name);
      // Clear location validation error immediately after a valid selection
      try {
        form.setFields([{ name: 'location', errors: [] }]);
      } catch (e) {
        // no-op
      }
    } else {
      handleFieldChange('location', '');
      setSelectedLocation(null);
      form.setFieldValue('location', '');
    }
  }, [handleFieldChange, form]);

  // Memoized location selection handler for birth place
  const handleBirthPlaceSelection = React.useCallback((location) => {
    if (location) {
      // Store the location object in selectedBirthLocation for birth_location payload
      setSelectedBirthLocation(location);
      // Store birthPlace as a string for birth_place payload
      const birthPlaceString = location.display_place || location.displayPlace || location.name || location.city?.label || location.city?.city?.label || '';
      handleFieldChange('birthPlace', birthPlaceString);
      form.setFieldValue('birthPlace', birthPlaceString);
    } else {
      handleFieldChange('birthPlace', '');
      setSelectedBirthLocation(null);
      form.setFieldValue('birthPlace', '');
    }
  }, [handleFieldChange, form]);

  // Memoized location selection handler for family location
  // Family location is stored as a simple string, not a complex object
  const handleFamilyLocationSelection = React.useCallback((location) => {
    if (location) {
      // For family location, we only store the string representation
      const locationString = location.display_place || location.displayPlace || location.name || '';
      handleFieldChange('familyLocation', locationString);
      form.setFieldValue('familyLocation', locationString);
    } else {
      handleFieldChange('familyLocation', '');
      form.setFieldValue('familyLocation', '');
    }
  }, [handleFieldChange, form]);

  // Auto scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Check URL parameters and set profile mode
  useEffect(() => {
    const profileId = params.id;
    const isDetailsMode = location.pathname.includes('/profile-details/');

    if (profileId) {
      setCurrentProfileId(profileId);
      setIsEditMode(true);

      if (isDetailsMode) {
        // Directly show details view if we're on profile-details route
        setShowDetailsView(true);
        fetchProfileDetails(profileId);
      } else {
        // Load profile data for editing - ensure details view is hidden
        setShowDetailsView(false);
        setSubmittedProfileData(null);
        fetchProfileForEdit(profileId);
      }
    } else {
      // Reset all states when no profile ID
      setIsEditMode(false);
      setCurrentProfileId(null);
      setShowDetailsView(false);
      setSubmittedProfileData(null);
    }
  }, [params, location.pathname, fetchProfileDetails, fetchProfileForEdit]);

  // Sync form values when formData changes
  useEffect(() => {
    // Filter out undefined values and convert objects to appropriate form values
    // Preserve empty strings and other values
    const filteredFormData = Object.fromEntries(
      Object.entries(formData).map(([key, value]) => {
        if (value === undefined) {
          return [key, undefined];
        }
        
        // Special handling for height and weight: always use code for Select component value matching
        if (key === 'height' || key === 'weight') {
          if (typeof value === 'object' && value !== null && value.code) {
            return [key, value.code];
          }
          return [key, value]; // Keep as is if it's already a code string
        }
        
        // Special handling for birthPlace: always use string value, not object
        if (key === 'birthPlace') {
          if (typeof value === 'object' && value !== null) {
            return [key, value.displayPlace || value.display_place || value.name || value.label || ''];
          }
          return [key, value]; // Keep as is if it's already a string
        }
        
        // If value is an object with label property, use the label for form display
        if (typeof value === 'object' && value !== null && value.label) {
          return [key, value.label];
        }
        
        return [key, value];
      }).filter(([key, value]) => value !== undefined)
    );
    form.setFieldsValue(filteredFormData);
  }, [form, formData]);

  // Handle caste prefilling when caste data loads
  useEffect(() => {
    // Only attempt prefilling if:
    // 1. We have castes loaded
    // 2. We have pending caste data
    // 3. Religion is set (castes are filtered by religion)
    // 4. Caste is not already set
    // 5. Religion matches the stored religion code (to ensure we're matching against correct caste list)
    // 6. We're in prefilling mode OR castes just finished loading
    if (
      castes && 
      castes.length > 0 && 
      pendingCasteDataRef.current && 
      formData.religion && 
      !formData.caste &&
      !castesLoading &&
      (!pendingCasteDataRef.current.religionCode || pendingCasteDataRef.current.religionCode === formData.religion)
    ) {
      const pendingCaste = pendingCasteDataRef.current;
      
      // Find the matching caste from the loaded data with multiple matching strategies
      // Strategy 1: Exact code match (most reliable)
      let matchingCaste = castes.find(caste => 
        caste.code === pendingCaste.code || 
        String(caste.code) === String(pendingCaste.code)
      );

      // Strategy 2: Exact label match
      if (!matchingCaste && pendingCaste.label) {
        matchingCaste = castes.find(caste => 
          caste.label === pendingCaste.label ||
          String(caste.label) === String(pendingCaste.label)
        );
      }

      // Strategy 3: Case-insensitive label match
      if (!matchingCaste && pendingCaste.label) {
        matchingCaste = castes.find(caste => 
          caste.label?.toLowerCase().trim() === pendingCaste.label?.toLowerCase().trim()
        );
      }

      // Strategy 4: ID match (if available)
      if (!matchingCaste && pendingCaste.id) {
        matchingCaste = castes.find(caste => 
          caste.id === pendingCaste.id ||
          String(caste.id) === String(pendingCaste.id)
        );
      }

      // Strategy 5: Partial code match (for codes like "HIN-25" matching "HIN-25" or just "25")
      if (!matchingCaste && pendingCaste.code) {
        const codeParts = String(pendingCaste.code).split('-');
        if (codeParts.length > 1) {
          // Try matching the full code first, then just the suffix
          matchingCaste = castes.find(caste => {
            const casteCodeParts = String(caste.code || '').split('-');
            return (
              casteCodeParts.length > 1 && 
              codeParts[codeParts.length - 1] === casteCodeParts[casteCodeParts.length - 1]
            );
          });
        }
      }

      if (matchingCaste) {
        // Use the code as the value (Select component expects code)
        const casteValue = matchingCaste.code || matchingCaste.id;
        
        // Update both form and formData
        form.setFieldValue('caste', casteValue);
        handleFieldChange('caste', casteValue);

        // Clear the pending data
        pendingCasteDataRef.current = null;
        
        // Clear the safety timeout if it exists
        if (prefillingTimeoutRef.current) {
          clearTimeout(prefillingTimeoutRef.current);
          prefillingTimeoutRef.current = null;
        }
        
        // Clear prefilling flag after a short delay to ensure all updates are complete
        setTimeout(() => {
          setIsPrefilling(false);
        }, 500);
      } else if (isPrefilling) {
        // If we're still in prefilling mode and didn't find a match, 
        // keep trying - castes might still be loading or filtering
        // Don't clear pending data or prefilling flag yet
        // The timeout will handle clearing if we can't find a match
      } else {
        // If we're not in prefilling mode and didn't find a match,
        // clear pending data after a delay
        setTimeout(() => {
          if (pendingCasteDataRef.current && !formData.caste) {
            pendingCasteDataRef.current = null;
          }
        }, 2000);
      }
    }
  }, [castes, castesLoading, form, handleFieldChange, formData.caste, formData.religion, isPrefilling]);


  // Handle image selection
  const handleImageChange = (images) => {
    setSelectedImages(images);
    setImageError(false);
    setImageValidationError(false); // Clear validation error when images are selected

    // If all images are removed, clear uploaded state and success message
    if (!images || images.length === 0) {
      setImagesUploaded(false);
      setUploadedImageUrls([]);
      setShowUploadButton(false);
      return;
    }

    // Check if any new images (with files) are selected
    const hasNewImages = images.some(img => img.file && img.file instanceof File);
    setShowUploadButton(hasNewImages);

    // Sync uploadedImageUrls with selectedImages to ensure consistency
    // Only include images that have s3Url or url (uploaded images)
    const uploadedUrls = images
      .filter(img => img.s3Url || img.url)
      .map(img => ({
        id: img.id || `img_${Date.now()}`,
        fileName: img.fileName || img.name || 'image.jpg',
        originalName: img.originalName || img.name || 'image.jpg',
        s3Url: img.s3Url || img.url,
        contentType: img.contentType || 'image/jpeg',
        fileSize: img.fileSize || 0,
        uploadType: img.uploadType || 'jpeg',
        status: 'uploaded',
        createdAt: img.createdAt || new Date().toISOString()
      }));

    setUploadedImageUrls(uploadedUrls);

    // If only existing images (no files), keep uploaded state
    if (!hasNewImages && images.length > 0) {
      setImagesUploaded(true);
    } else if (hasNewImages) {
      setImagesUploaded(false);
    }
  };

  // Handle horoscope image selection
  const handleHoroscopeImageChange = (images) => {
    setSelectedHoroscopeImages(images);
    setHoroscopeImageError(false);

    // If all horoscope images are removed, clear uploaded state and success message
    if (!images || images.length === 0) {
      setHoroscopeImagesUploaded(false);
      setUploadedHoroscopeUrls([]);
      setShowHoroscopeUploadButton(false);
      return;
    }

    // Check if any new images (with files) are selected
    const hasNewImages = images.some(img => img.file && img.file instanceof File);
    setShowHoroscopeUploadButton(hasNewImages);

    // If only existing images (no files), keep uploaded state
    if (!hasNewImages && images.length > 0) {
      setHoroscopeImagesUploaded(true);
    } else if (hasNewImages) {
      setHoroscopeImagesUploaded(false);
      setUploadedHoroscopeUrls([]);
    }
  };

  // Handle image validation
  const validateImages = React.useCallback(() => {
    if (selectedImages.length === 0) {
      setImageError(true);
      return false;
    }

    // Check if there are any new images that need to be uploaded
    const hasNewImages = selectedImages.some(img => img.file && img.file instanceof File);
    if (hasNewImages && !imagesUploaded) {
      message.error('Please upload the selected images before submitting the form.');
      return false;
    }

    // Ensure uploadedImageUrls has actual S3 URLs
    if (uploadedImageUrls.length === 0) {
      message.error('Please upload the images before submitting the form.');
      setImageValidationError(true);
      return false;
    }

    return true;
  }, [selectedImages, imagesUploaded, uploadedImageUrls]);

  // Validate basic details for form submission
  const validateBasicDetails = React.useCallback(() => {
    const basicFields = {
      fullName: formData.fullName?.trim(),
      motherTongue: formData.motherTongue,
      gender: formData.gender,
      dob: formData.dob,
      maritalStatus: formData.maritalStatus,
      religion: formData.religion,
      caste: formData.caste,
      height: formData.height,
      isDisabled: formData.isDisabled,
      location: formData.location?.trim(),
      job: formData.job,
      education: formData.education,
      bio: formData.bio?.trim()
    };

    // Check if all basic fields are filled
    const isBasicDetailsFilled = Object.values(basicFields).every(value =>
      value !== null && value !== undefined && value !== ''
    );

    // Check if bio meets minimum length requirement
    const isBioValid = basicFields.bio && basicFields.bio.length >= 10;

    // Check if images are uploaded
    const isImageUploaded = selectedImages.length > 0 && imagesUploaded;

    return isBasicDetailsFilled && isBioValid && isImageUploaded;
  }, [formData, selectedImages, imagesUploaded]);


  // Monitor Redux state changes for debugging
  React.useEffect(() => {
  }, [mobListingData]);


  // Function to trigger image validation
  const triggerImageValidation = React.useCallback(() => {
    const isImageValidationError = selectedImages.length === 0 || !imagesUploaded;
    setImageValidationError(isImageValidationError);
    return !isImageValidationError;
  }, [selectedImages, imagesUploaded]);

  // Function to clear form validation errors for properly filled fields
  const clearFormValidationErrors = React.useCallback(() => {
    const fieldsToCheck = [
      'fullName', 'motherTongue', 'gender', 'dob', 'maritalStatus', 
      'religion', 'caste', 'height', 'isDisabled', 'location', 
      'job', 'education', 'bio'
    ];

    fieldsToCheck.forEach(fieldName => {
      const fieldValue = formData[fieldName];
      
      // Check if field is properly filled
      let isFieldValid = false;
      
      if (fieldName === 'bio') {
        // Bio needs to be at least 10 characters
        isFieldValid = fieldValue && fieldValue.trim().length >= 10;
      } else if (fieldName === 'location') {
        // Location needs to be a non-empty string
        isFieldValid = fieldValue && fieldValue.trim() !== '';
      } else {
        // Other fields need to have a value
        isFieldValid = fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
      }

      // Clear validation error if field is valid
      if (isFieldValid) {
        form.setFields([{
          name: fieldName,
          errors: []
        }]);
      }
    });
  }, [formData, form]);

  // Function to set mock API data for preview (using the provided API response)
  const setMockListingData = React.useCallback(() => {
    const mockApiData = [
      {
        "_id": "65b75aad2fd15c1e725937d2",
        "phone": "",
        "country_code": "+91",
        "short_listed_by": [],
        "profile_verified": false,
        "profile_images": [
          {
            "id": "65f81da3f5fca5d782a2ef5c",
            "is_profile": true,
            "og_key": "65b75aad2fd15c1e725937d2/1710759331079.jpg",
            "original": "https://india-s3-dev-photo-video-uploads.s3.ap-south-1.amazonaws.com/66e12552c0164ed9ee4d921e/1726031326019.jpg",
            "thumb_key": "65b75aad2fd15c1e725937d2/1710759331079-thumbnail.jpg",
            "thumbnail": "https://india-s3-dev-photo-video-uploads.s3.ap-south-1.amazonaws.com/66e12552c0164ed9ee4d921e/1726031326019.jpg",
            "verification_status": 1,
            "_id": "65f81da3f5fca5d782a2ef5d"
          }
        ],
        "updated_at": "2024-04-15T05:27:47.067Z",
        "age": 18,
        "city": {
          "label": "Cheruthuruthy",
          "code": "KL020"
        },
        "country": {
          "label": "India",
          "code": "IN"
        },
        "dob": "2006-03-18T00:00:00.000Z",
        "gender": {
          "label": "Female",
          "code": "1"
        },
        "location": "Kerala Kalamandalam",
        "name": "Sneha",
        "religion": {
          "label": "Buddhist",
          "code": "BUD"
        },
        "state": {
          "label": "Kerala",
          "code": "KL"
        },
        "astro_sign": "Pisces",
        "bio": "Hello, I am Bzbxnnx. I hail from Cheruthuruthy. I am looking for a Theravada groom.",
        "caste": {
          "code": "BUD-0",
          "label": "Theravada"
        },
        "height": {
          "label": "126 cm / 4 ft 2 in",
          "code": "126"
        },
        "marital_status": {
          "code": "0",
          "label": "Single"
        },
        "job": {
          "label": "Adjudicator",
          "code": "4"
        },
        "lumi_id": "LBF105FBIN",
        "preferences": {
          "full_name": true,
          "dob": true,
          "location": true,
          "notification": true,
          "show_contact": false
        },
        "last_login": "2024-04-15T05:27:52.894Z",
        "is_connected": false,
        "isContactPurchased": false,
        "type": "profile",
        "is_shortlisted": false,
        "status": "connect",
        "phone_number_length": 10
      },
      {
        "_id": "65b7c0a52fd15c1e72593a99",
        "phone": "",
        "country_code": "+91",
        "short_listed_by": [],
        "profile_verified": false,
        "profile_images": [
          {
            "id": "65b7c0fc00fb4ce3b114a6b6",
            "is_profile": true,
            "og_key": "65b7c0a52fd15c1e72593a99/1706541308387.jpg",
            "original": "https://india-s3-dev-photo-video-uploads.s3.ap-south-1.amazonaws.com/65b7c0a52fd15c1e72593a99/1706541308387.jpg",
            "thumb_key": "65b7c0a52fd15c1e72593a99/1706541308387-thumbnail.jpg",
            "thumbnail": "https://india-s3-dev-photo-video-uploads.s3.ap-south-1.amazonaws.com/65b7c0a52fd15c1e72593a99/1706541308387-thumbnail.jpg",
            "verification_status": 1,
            "_id": "65b7c0fc00fb4ce3b114a6b7"
          }
        ],
        "updated_at": "2024-06-24T10:09:39.373Z",
        "age": 18,
        "city": {
          "label": "Kochi",
          "code": "KL016"
        },
        "country": {
          "label": "India",
          "code": "IN"
        },
        "dob": "2006-01-29T00:00:00.000Z",
        "gender": {
          "label": "Female",
          "code": "1"
        },
        "location": "Kochi",
        "name": "Aswathy",
        "religion": {
          "label": "Hindu",
          "code": "HIN"
        },
        "state": {
          "label": "Kerala",
          "code": "KL"
        },
        "astro_sign": "Aquarius",
        "bio": "Hello, I am Aswathy. I hail from Kochi. I am looking for an Adi Dravidar groom.",
        "caste": {
          "label": "Adi Dravidar",
          "code": "HIN-163"
        },
        "height": {
          "label": "129 cm / 4 ft 3 in",
          "code": "129"
        },
        "marital_status": {
          "label": "Single",
          "code": "0"
        },
        "job": {
          "label": "Adult Education Teacher",
          "code": "8"
        },
        "lumi_id": "LBF10581IN",
        "preferences": {
          "full_name": true,
          "dob": true,
          "location": true,
          "notification": true,
          "show_contact": false
        },
        "last_login": "2024-06-24T10:09:37.090Z",
        "is_connected": false,
        "isContactPurchased": false,
        "type": "profile",
        "is_shortlisted": false,
        "status": "connect",
        "phone_number_length": 10
      }
    ];

    // Dispatch action to update Redux state with mock data
    dispatch({
      type: 'mobileListing/getMobileListingData/fulfilled',
      payload: mockApiData
    });

    // Also set local state for immediate use
    setMockListingDataState(mockApiData);

  }, [dispatch]);

  // Preview functionality
  const showPreviewModal = () => {
    const basicOk = validateBasicDetails();
    if (!basicOk) {
      setPreviewErrorModalOpen(true);
      return;
    }

    // Set mock API data for preview instead of fetching from API
    setMockListingData();

    // Load mobile data for preview
    // dispatch(getMobileData(false));

    setIsPreviewModalOpen(true);
  };

  const handlePreviewCancel = () => {
    setIsPreviewModalOpen(false);
    setPreviewErrorModalOpen(false);
  };

  // Enable submit - only disable if new images are selected but not uploaded
  const canSubmitForm = React.useMemo(() => {
    // Check regular images
    if (selectedImages.length > 0) {
      const hasNewImages = selectedImages.some(img => img.file && img.file instanceof File);
      if (hasNewImages && !imagesUploaded) {
        return false;
      }
    }

    // Check horoscope images
    if (selectedHoroscopeImages.length > 0) {
      const hasNewHoroscopeImages = selectedHoroscopeImages.some(img => img.file && img.file instanceof File);
      if (hasNewHoroscopeImages && !horoscopeImagesUploaded) {
        return false;
      }
    }

    // If all images are uploaded or no new images selected, allow submission
    return true;
  }, [imagesUploaded, selectedImages, horoscopeImagesUploaded, selectedHoroscopeImages]);



  // Handle image upload to API - FormData approach with clean API endpoint usage
  const handleImageUpload = async () => {
    if (selectedImages.length === 0) {
      message.error('Please select images first.');
      return;
    }

    setImageUploadLoading(true);

    try {
      // Validate all images have files
      const validImages = selectedImages.filter(image => {
        if (!image.file || !(image.file instanceof File)) {
          return false;
        }
        if (image.file.size === 0) {
          return false;
        }
        return true;
      });

      if (validImages.length === 0) {
        throw new Error('No valid files found. Please reselect the images.');
      }

      // if (validImages.length !== selectedImages.length) {
      //   message.warning(`${selectedImages.length - validImages.length} invalid files were skipped.`);
      // }

      // Create upload promises for parallel processing
      const uploadPromises = validImages.map(async (image, index) => {
        try {

          // Create FormData using utility function
          const formData = createImageUploadFormData(image.file);

          // Make API call using axios with FormData
          const response = await axios.post(API_ENDPOINTS.UPLOAD.FILE, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            }
          });

          if (response.success && response.data) {
            return {
              id: response.data.id,
              fileName: response.data.fileName,
              originalName: response.data.originalName,
              s3Url: response.data.s3Url,
              contentType: response.data.contentType,
              fileSize: response.data.fileSize,
              uploadType: response.data.uploadType,
              status: response.data.status,
              createdAt: response.data.createdAt
            };
          } else {
            throw new Error(`Upload failed: ${response.message || 'Unknown error'}`);
          }

        } catch (uploadError) {
          const errorMessage = uploadError.response?.data?.message || uploadError.message || 'Unknown upload error';
          throw new Error(`Failed to upload ${image.file.name}: ${errorMessage}`);
        }
      });

      // Execute all uploads in parallel

      // Use Promise.allSettled to handle partial failures gracefully
      const uploadResults = await Promise.allSettled(uploadPromises);

      // Process results and separate successful from failed uploads
      const successfulUploads = [];
      const failedUploads = [];

      uploadResults.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value) {
          successfulUploads.push(result.value);
        } else {
          failedUploads.push({
            fileName: validImages[index]?.file?.name || `Image ${index + 1}`,
            error: result.reason?.message || 'Unknown error'
          });
        }
      });

      // Sync selectedImages with the uploaded s3Urls to keep them in sync
      const updatedSelectedImages = selectedImages.map(img => {
        // If this image was just uploaded, update it with the s3Url
        const uploadedImg = successfulUploads.find(uploaded => 
          uploaded.fileName === img.name || uploaded.originalName === img.name
        );
        if (uploadedImg) {
          return {
            ...img,
            s3Url: uploadedImg.s3Url,
            url: uploadedImg.s3Url,
            status: 'done',
            file: null // Clear the file object after upload
          };
        }
        return img;
      });
      setSelectedImages(updatedSelectedImages);

      // Rebuild uploadedImageUrls based on the order of selectedImages to maintain consistency
      // Only include images that have s3Url (both existing and newly uploaded)
      const finalUploadedUrls = updatedSelectedImages
        .filter(img => img.s3Url || img.url)
        .map(img => {
          // Check if this image is already in uploadedImageUrls (existing image)
          const existingUpload = uploadedImageUrls.find(u => u.s3Url === (img.s3Url || img.url));
          if (existingUpload) {
            return existingUpload;
          }
          // Check if this is a newly uploaded image
          const newUpload = successfulUploads.find(uploaded => uploaded.s3Url === img.s3Url);
          if (newUpload) {
            return newUpload;
          }
          // Fallback: create from img data
          return {
            id: img.id || `img_${Date.now()}`,
            fileName: img.fileName || img.name || 'image.jpg',
            originalName: img.originalName || img.name || 'image.jpg',
            s3Url: img.s3Url || img.url,
            contentType: img.contentType || 'image/jpeg',
            fileSize: img.fileSize || 0,
            uploadType: img.uploadType || 'jpeg',
            status: 'uploaded',
            createdAt: img.createdAt || new Date().toISOString()
          };
        });

      setUploadedImageUrls(finalUploadedUrls);

      if (successfulUploads.length > 0) {
        setImagesUploaded(true);
        setShowUploadButton(false);
        setImageValidationError(false); // Clear validation error when images are successfully uploaded
      }

      // Show appropriate success/warning messages
      if (failedUploads.length === 0) {
        message.success(`Successfully uploaded all ${successfulUploads.length} image(s)!`);
      } else if (successfulUploads.length > 0) {
        message.warning(`Uploaded ${successfulUploads.length} of ${uploadPromises.length} images. ${failedUploads.length} failed.`);
      } else {
        throw new Error(`All ${uploadPromises.length} image uploads failed.`);
      }

    } catch (error) {
      message.error('Failed to upload images. Please try again.');
    } finally {
      setImageUploadLoading(false);
    }
  };

  // Handle horoscope image upload to API
  const handleHoroscopeImageUpload = async () => {
    if (selectedHoroscopeImages.length === 0) {
      message.error('Please select horoscope documents first.');
      return;
    }

    setHoroscopeUploadLoading(true);

    try {
      // Validate all documents have files
      const validDocuments = selectedHoroscopeImages.filter(document => {
        if (!document.file || !(document.file instanceof File)) {
          return false;
        }
        if (document.file.size === 0) {
          return false;
        }
        return true;
      });

      if (validDocuments.length === 0) {
        throw new Error('No valid files found. Please reselect the documents.');
      }

      if (validDocuments.length !== selectedHoroscopeImages.length) {
        message.warning(`${selectedHoroscopeImages.length - validDocuments.length} invalid files were skipped.`);
      }

      // Create upload promises for parallel processing
      const uploadPromises = validDocuments.map(async (document, index) => {
        try {

          // Create FormData using utility function
          const formData = createImageUploadFormData(document.file);

          // Make API call using axios with FormData
          const response = await axios.post(API_ENDPOINTS.UPLOAD.FILE, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            }
          });

          if (response.success && response.data) {
            return {
              id: response.data.id,
              fileName: response.data.fileName,
              originalName: response.data.originalName,
              s3Url: response.data.s3Url,
              contentType: response.data.contentType,
              fileSize: response.data.fileSize,
              uploadType: response.data.uploadType,
              status: response.data.status,
              createdAt: response.data.createdAt
            };
          } else {
            throw new Error(`Upload failed: ${response.message || 'Unknown error'}`);
          }

        } catch (uploadError) {
          const errorMessage = uploadError.response?.data?.message || uploadError.message || 'Unknown upload error';
          throw new Error(`Failed to upload ${document.file.name}: ${errorMessage}`);
        }
      });

      // Execute all uploads in parallel

      // Use Promise.allSettled to handle partial failures gracefully
      const uploadResults = await Promise.allSettled(uploadPromises);

      // Process results and separate successful from failed uploads
      const successfulUploads = [];
      const failedUploads = [];

      uploadResults.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value) {
          successfulUploads.push(result.value);
        } else {
          failedUploads.push({
            fileName: validDocuments[index]?.file?.name || `Document ${index + 1}`,
            error: result.reason?.message || 'Unknown error'
          });
        }
      });

      // Sync selectedHoroscopeImages with the uploaded s3Urls to keep them in sync
      const updatedSelectedHoroscopeImages = selectedHoroscopeImages.map(img => {
        // If this image was just uploaded, update it with the s3Url
        const uploadedImg = successfulUploads.find(uploaded => 
          uploaded.fileName === img.name || uploaded.originalName === img.name
        );
        if (uploadedImg) {
          return {
            ...img,
            s3Url: uploadedImg.s3Url,
            url: uploadedImg.s3Url,
            status: 'done',
            file: null // Clear the file object after upload
          };
        }
        return img;
      });
      setSelectedHoroscopeImages(updatedSelectedHoroscopeImages);

      // Rebuild uploadedHoroscopeUrls based on the order of selectedHoroscopeImages to maintain consistency
      // Only include images that have s3Url (both existing and newly uploaded)
      const finalUploadedHoroscopeUrls = updatedSelectedHoroscopeImages
        .filter(img => img.s3Url || img.url)
        .map(img => {
          // Check if this image is already in uploadedHoroscopeUrls (existing image)
          const existingUpload = uploadedHoroscopeUrls.find(u => u.s3Url === (img.s3Url || img.url));
          if (existingUpload) {
            return existingUpload;
          }
          // Check if this is a newly uploaded image
          const newUpload = successfulUploads.find(uploaded => uploaded.s3Url === img.s3Url);
          if (newUpload) {
            return newUpload;
          }
          // Fallback: create from img data
          return {
            id: img.id || `img_${Date.now()}`,
            fileName: img.fileName || img.name || 'horoscope.jpg',
            originalName: img.originalName || img.name || 'horoscope.jpg',
            s3Url: img.s3Url || img.url,
            contentType: img.contentType || 'image/jpeg',
            fileSize: img.fileSize || 0,
            uploadType: img.uploadType || 'jpeg',
            status: 'uploaded',
            createdAt: img.createdAt || new Date().toISOString()
          };
        });

      setUploadedHoroscopeUrls(finalUploadedHoroscopeUrls);

      if (successfulUploads.length > 0) {
        setHoroscopeImagesUploaded(true);
        setShowHoroscopeUploadButton(false);
      }

      // Show appropriate success/warning messages
      if (failedUploads.length === 0) {
        message.success(`Successfully uploaded all ${successfulUploads.length} horoscope document(s)!`);
      } else if (successfulUploads.length > 0) {
        message.warning(`Uploaded ${successfulUploads.length} of ${uploadPromises.length} documents. ${failedUploads.length} failed.`);
      } else {
        throw new Error(`All ${uploadPromises.length} horoscope document uploads failed.`);
      }

    } catch (error) {
      message.error('Failed to upload horoscope documents. Please try again.');
    } finally {
      setHoroscopeUploadLoading(false);
    }
  };


  // Helper function to extract sub_division options from nested API response
  const extractSubDivisionOptions = React.useCallback((apiData) => {
    if (!apiData || !Array.isArray(apiData) || apiData.length === 0) {
      return [];
    }

    // Get the first item from the data array (which contains the sub_division)
    const firstItem = apiData[0];
    if (firstItem && firstItem.sub_division && Array.isArray(firstItem.sub_division)) {
      return firstItem.sub_division
        .sort((a, b) => a.order - b.order) // Sort by order field
        .map(item => ({
          id: item.id,
          label: item.label,
          value: item.code,
          order: item.order
        }));
    }

    return [];
  }, []);

  // Helper function to find master data by code/id/label and return code + label
  const findMasterDataByValue = React.useCallback((masterData, searchValue, isSubDivision = false) => {
    if (!masterData || !Array.isArray(masterData) || !searchValue) return null;

    // Normalize incoming value: allow {code,label}, string, number
    let searchCode = '';
    let searchLabel = '';
    if (typeof searchValue === 'object') {
      searchCode = (searchValue.code || '').toString();
      searchLabel = (searchValue.label || '').toString();
    } else {
      searchCode = searchValue.toString();
      searchLabel = searchValue.toString();
    }
    const normCode = searchCode.trim().toLowerCase();
    const normLabel = searchLabel.trim().toLowerCase();

    if (isSubDivision) {
      // For sub_division data (food, drinking, smoking, ideologies)
      const firstItem = masterData[0];
      if (firstItem && Array.isArray(firstItem.sub_division)) {
        const found = firstItem.sub_division.find(item => {
          const itemCode = (item.code || '').toString().trim().toLowerCase();
          const itemId = (item.id || '').toString().trim().toLowerCase();
          const itemLabel = (item.label || '').toString().trim().toLowerCase();
          return itemCode === normCode || itemId === normCode || itemLabel === normLabel;
        });
        if (found) return { code: found.code, label: found.label };
      }
    } else {
      // For regular master data - search by code, id, or label (case-insensitive)
      const found = masterData.find(item => {
        const itemCode = (item.code || '').toString().trim().toLowerCase();
        const itemId = (item.id || '').toString().trim().toLowerCase();
        const itemLabel = (item.label || item.name || '').toString().trim().toLowerCase();
        return itemCode === normCode || itemId === normCode || itemLabel === normLabel;
      });
      if (found) {
        return {
          code: found.code || found.id,
          label: found.label || found.name
        };
      }
    }

    return null;
  }, []);

  // Transform form data for Advertisement component display
  const transformFormDataForAdvertisement = React.useCallback((formValues) => {

    // Calculate age from date of birth
    const calculateAge = (dob) => {
      if (!dob) return null;
      const today = new Date();
      const birthDate = new Date(dob);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    };

    const heightData = findMasterDataByValue(heights, formValues.height);

    const advertisementData = {
      name: formValues.fullName || '',
      age: calculateAge(formValues.dob),
      lumi_id: 'AD' + Math.floor(Math.random() * 1000000), // Generate a random ID for preview
      height: heightData,
      marital_status: findMasterDataByValue(maritalStatuses, formValues.maritalStatus),
      religion: findMasterDataByValue(religions, formValues.religion),
      caste: findMasterDataByValue(castes, formValues.caste),
      job: findMasterDataByValue(jobs, formValues.job),
      city: selectedLocation ? (selectedLocation.city?.label ? selectedLocation.city : { label: selectedLocation.city }) : null,
      state: selectedLocation ? (selectedLocation.state?.label ? selectedLocation.state : { label: selectedLocation.state }) : null,
      location: selectedLocation ? (selectedLocation.displayPlace || selectedLocation.displayName || formValues.location) : formValues.location
    };

    return advertisementData;
  }, [heights, maritalStatuses, religions, castes, jobs, selectedLocation, findMasterDataByValue]);

  // Transform form data to API payload structure
  const transformFormDataToPayload = React.useCallback((formValues) => {


    // Transform basic profile data
    const payload = {
      name: formValues.fullName || '',
      dob: formValues.dob ? new Date(formValues.dob).toISOString() : null,
      physically_disabled: formValues.isDisabled === 'Yes',
      bio: formValues.bio || '',
      // Ensure birth_place is always a string, not an object
      birth_place: (() => {
        const birthPlaceValue = formValues.birthPlace;
        if (!birthPlaceValue) return '';
        // If it's an object, extract the string value
        if (typeof birthPlaceValue === 'object' && birthPlaceValue !== null) {
          return birthPlaceValue.displayPlace || birthPlaceValue.display_place || birthPlaceValue.name || birthPlaceValue.label || '';
        }
        // If it's already a string, return it
        return String(birthPlaceValue);
      })(),
    };

    // Add weight field if provided
    if (formValues.weight) {
      let weightData = null;
      if (typeof formValues.weight === 'object' && formValues.weight !== null) {
        // If weight is already a masterdata object, use it directly
        weightData = formValues.weight;
      } else {
        // If weight is a string (code), find it in masterdata
        weightData = findMasterDataByValue(weightOptions, formValues.weight);
      }
      
      if (weightData) {
        // Remove id field from weight data when editing
        const { id, ...weightWithoutId } = weightData;
        payload.weight = weightWithoutId;
      } else {
        // Fallback: if weight data not found but value exists, create basic structure
        // This ensures weight is always included in payload if it exists in form
        payload.weight = {
          code: formValues.weight,
          label: formValues.weight
        };
      }
    }

    // Transform gender
    const genderCode = formValues.gender === 'Male' ? '0' : formValues.gender === 'Female' ? '1' : '';
    const genderLabel = formValues.gender === 'Male' ? 'Male' : formValues.gender === 'Female' ? 'Female' : '';
    
    if (genderCode && genderLabel) {
      payload.gender = {
        code: genderCode,
        label: genderLabel
      };
    }

    // Transform mother tongue
    const motherTongueData = findMasterDataByValue(languages, formValues.motherTongue);
    if (motherTongueData) {
      // Remove id field from mother tongue data when editing
      const { id, ...motherTongueWithoutId } = motherTongueData;
      payload.mother_tongue = motherTongueWithoutId;
    } else if (formValues.motherTongue) {
      // Fallback: if mother tongue data not found but value exists, create basic structure
      payload.mother_tongue = {
        code: formValues.motherTongue,
        label: formValues.motherTongue
      };
    }

    // Transform marital status
    const maritalStatusData = findMasterDataByValue(maritalStatuses, formValues.maritalStatus);
    if (maritalStatusData) {
      // Remove id field from marital status data when editing
      const { id, ...maritalStatusWithoutId } = maritalStatusData;
      payload.marital_status = maritalStatusWithoutId;
    }

    // Transform religion
    const religionData = findMasterDataByValue(religions, formValues.religion);
    if (religionData) {
      // Remove id field from religion data when editing
      const { id, ...religionWithoutId } = religionData;
      payload.religion = religionWithoutId;
    } else if (formValues.religion) {
      // Fallback: if religion data not found but value exists, create basic structure
      // This handles cases where the stored value doesn't match master data exactly
      payload.religion = {
        code: formValues.religion,
        label: formValues.religion
      };
    }

      // Transform caste
      let casteData = null;
      if (typeof formValues.caste === 'object' && formValues.caste !== null) {
        // If caste is already a masterdata object, use it directly
        casteData = formValues.caste;
      } else {
        // If caste is a string, find it in masterdata
        casteData = findMasterDataByValue(castes, formValues.caste);
      }
      
      if (casteData) {
        // Remove id field from caste data when editing
        const { id, ...casteWithoutId } = casteData;
        payload.caste = casteWithoutId;
      } else if (formValues.caste) {
        // Fallback: if caste data not found but value exists, create basic structure
        payload.caste = {
          code: formValues.caste,
          label: formValues.caste
        };
      }

      // Transform height
      let heightData = null;
      if (typeof formValues.height === 'object' && formValues.height !== null) {
        // If height is already a masterdata object, use it directly
        heightData = formValues.height;
      } else {
        // If height is a string (code), find it in masterdata
        heightData = findMasterDataByValue(heights, formValues.height);
      }
      
      if (heightData) {
        // Remove id field from height data when editing
        const { id, ...heightWithoutId } = heightData;
        payload.height = heightWithoutId;
      } else if (formValues.height) {
        // Fallback: if height data not found but value exists, create basic structure
        // This ensures height is always included in payload if it exists in form
        payload.height = {
          code: formValues.height,
          label: formValues.height
        };
      }

    // Transform birth star
    const birthStarData = findMasterDataByValue(birthStars, formValues.birthStar);
    if (birthStarData) {
      // Remove id field from birth star data when editing
      const { id, ...birthStarWithoutId } = birthStarData;
      payload.birth_star = birthStarWithoutId;
    }

      // Transform job
      let jobData = null;
      if (typeof formValues.job === 'object' && formValues.job !== null) {
        // If job is already a masterdata object, use it directly
        jobData = formValues.job;
      } else {
        // If job is a string, find it in masterdata
        jobData = findMasterDataByValue(jobs, formValues.job);
      }
      
      if (jobData) {
        // Remove id field from job data when editing
        const { id, ...jobWithoutId } = jobData;
        payload.job = jobWithoutId;
      }

      // Transform education
      let educationData = null;
      if (typeof formValues.education === 'object' && formValues.education !== null) {
        // If education is already a masterdata object, use it directly
        educationData = formValues.education;
      } else {
        // If education is a string, find it in masterdata
        educationData = findMasterDataByValue(educations, formValues.education);
      }
      
      if (educationData) {
        // Remove id field from education data when editing
        const { id, ...educationWithoutId } = educationData;
        payload.education = educationWithoutId;
      }

    // Transform interests: use the stored complete objects directly
    if (selectedInterestsData && selectedInterestsData.length > 0) {
      payload.interests = selectedInterestsData;
    } else {
      // Fallback: try to process from form values (for backward compatibility)
      const sourceInterests = (Array.isArray(formValues.interests) && formValues.interests.length > 0)
        ? formValues.interests
        : (Array.isArray(interests) ? interests : []);

      if (sourceInterests.length > 0) {
        // Check if interestsList is loaded
        if (!interestsList || interestsList.length === 0) {
          // Skip processing if interestsList is not loaded
        } else {
          const interestData = sourceInterests.map((item) => {
            // If already an object with code/label, normalize and use it
            if (item && typeof item === 'object') {
              const code = item.code || '';
              const label = item.label || (typeof item.value === 'string' ? item.value : '');
              if (code && label) return { code, label };
              // Try resolving via masterdata if only label provided
              const resolved = findMasterDataByValue(interestsList, label || code);
              if (resolved) return { code: resolved.code, label: resolved.label };
              return { code: code || '', label: label || '' };
            }

            // Now item should be an ID (string or number) - find the interest by ID
            const interestId = typeof item === 'string' ? item : String(item ?? '');
            
            const found = interestsList.find(interest => interest.id === interestId || String(interest.id) === interestId);
            if (found && found.label) {
              return { code: found.code, label: found.label };
            }
            
            // Fallback: try to resolve as label/code (for backward compatibility)
            const searchValue = typeof item === 'string' ? item : String(item ?? '');
            const foundByValue = findMasterDataByValue(interestsList, searchValue);
            if (foundByValue && foundByValue.label) {
              return { code: foundByValue.code, label: foundByValue.label };
            }
            
            // Final fallback: skip this interest instead of storing invalid data
            return null;
          }).filter(Boolean); // Remove null entries

          payload.interests = interestData;
        }
      }
    }

    // Transform location/city data - only add if we have valid location data
    if (selectedLocation && selectedLocation.id && selectedLocation.id !== '') {
      // Ensure city ID is a valid 24-character ObjectID or use a fallback
      let cityId = selectedLocation.id;
      if (cityId.length !== 24) {
        // If the ID is not 24 characters, use a fallback ObjectID format
        // Pad with zeros or truncate to make it 24 characters
        if (cityId.length < 24) {
          cityId = cityId.padEnd(24, '0');
        } else {
          cityId = cityId.substring(0, 24);
        }
      }
      
      payload.city = {
        id: cityId,
        lat: selectedLocation.lat || '',
        lon: selectedLocation.lon || '',
        display_name: selectedLocation.display_name || '',
        display_place: selectedLocation.display_place || '',
        display_address: selectedLocation.display_address || '',
        name: selectedLocation.name || '',
        county: selectedLocation.county || '',
        zipcode: selectedLocation.zipcode || '',
        country: selectedLocation.country || { code: '', label: '' },
        state: selectedLocation.state || { code: '', label: '' },
        city: selectedLocation.city || {
          district: '',
          label: '',
          code: ''
        },
        geojson: selectedLocation.geojson || { type: '', coordinates: null },
        iteration: 0
      };
    }

    // Transform profile images - use uploadedImageUrls to ensure actual S3 URLs are sent
    if (uploadedImageUrls && uploadedImageUrls.length > 0) {
      payload.profile_images = uploadedImageUrls.map((img, index) => ({
        original: img.s3Url,
        is_profile: index === 0 // First image is profile image
      }));
    }

    // Transform habits (food, drinking, smoking, ideologies)
    const habits = [];

    // Food habit
    if (formValues.food) {
      const foodData = findMasterDataByValue(foodOptions, formValues.food, true);
      const foodItem = foodData || { code: String(formValues.food), label: String(formValues.food) };
      habits.push({
        code: "0",
        label: "Food|Habit",
        sub_division: [
          {
            label: foodItem.label,
            code: foodItem.code
          }
        ]
      });
    }

    // Drinking habit
    if (formValues.drinking) {
      const drinkingData = findMasterDataByValue(drinkingOptions, formValues.drinking, true);
      const drinkItem = drinkingData || { code: String(formValues.drinking), label: String(formValues.drinking) };
      habits.push({
        code: "1",
        label: "Drinking|Habit",
        sub_division: [
          {
            label: drinkItem.label,
            code: drinkItem.code
          }
        ]
      });
    }

    // Smoking habit
    if (formValues.smoking) {
      const smokingData = findMasterDataByValue(smokingOptions, formValues.smoking, true);
      const smokeItem = smokingData || { code: String(formValues.smoking), label: String(formValues.smoking) };
      habits.push({
        code: "2",
        label: "Smoking|Habit",
        sub_division: [
          {
            label: smokeItem.label,
            code: smokeItem.code
          }
        ]
      });
    }

    // Ideologies
    if (formValues.ideologies) {
      const ideologyData = findMasterDataByValue(ideologyOptions, formValues.ideologies, true);
      const ideologyItem = ideologyData || { code: String(formValues.ideologies), label: String(formValues.ideologies) };
      habits.push({
        code: "3",
        label: "Ideologies|& Beliefs",
        sub_division: [
          {
            label: ideologyItem.label,
            code: ideologyItem.code
          }
        ]
      });
    }

    if (habits.length > 0) {
      payload.habit = habits;
    }

    // Add birth time if provided - convert to proper time.Time format
    if (formValues.birthTime) {
      try {
        // Parse the time string (e.g., "09:30 AM" or "2:00 AM") and convert to ISO format
        const timeString = formValues.birthTime.trim();
        const [timePart, period] = timeString.split(' ');
        const [hours, minutes] = timePart.split(':');

        let hour24 = parseInt(hours);
        if (period === 'PM' && hour24 !== 12) {
          hour24 += 12;
        } else if (period === 'AM' && hour24 === 12) {
          hour24 = 0;
        }

        // Use UTC methods to create the date without timezone conversion issues
        // This ensures the time remains exactly as selected by the user
        const today = new Date();
        const birthDateTime = new Date(Date.UTC(
          today.getUTCFullYear(),
          today.getUTCMonth(),
          today.getUTCDate(),
          hour24,
          parseInt(minutes) || 0,
          0,
          0
        ));

        // Send as birth_date_time in ISO format (now it will maintain the exact time)
        payload.birth_date_time = birthDateTime.toISOString();
      } catch (error) {
        // Fallback to original string format
        payload.birth_time = formValues.birthTime;
      }
    }

    // Add family status if provided (for backward compatibility at root level)
    if (formValues.familyStatus) {
      const familyTypeData = findMasterDataByValue(familyTypes, formValues.familyStatus);
      if (familyTypeData) {
        payload.family_status = familyTypeData.code;
      }
    }

    // Add dosham/manglik if provided - send as string format
    if (formValues.dosham) {
      const doshamData = findMasterDataByValue(manglikOptions, formValues.dosham);
      if (doshamData) {
        // Send as manglik object with code and label
        payload.manglik = {
          code: doshamData.code,
          label: doshamData.label
        };
      } else {
        // Fallback to direct value if not found in master data
        payload.manglik = formValues.dosham;
      }
    }

    // Add birth location if provided (as structured object like city location)
    if (selectedBirthLocation && selectedBirthLocation.id && selectedBirthLocation.id !== '') {
      // Ensure birth location ID is a valid 24-character ObjectID or use a fallback
      let birthCityId = selectedBirthLocation.id;
      if (birthCityId.length !== 24) {
        if (birthCityId.length < 24) {
          birthCityId = birthCityId.padEnd(24, '0');
        } else {
          birthCityId = birthCityId.substring(0, 24);
        }
      }
      
      payload.birth_location = {
        id: birthCityId,
        lat: selectedBirthLocation.lat || '',
        lon: selectedBirthLocation.lon || '',
        display_name: selectedBirthLocation.display_name || '',
        display_place: selectedBirthLocation.display_place || '',
        display_address: selectedBirthLocation.display_address || '',
        name: selectedBirthLocation.name || '',
        county: selectedBirthLocation.county || '',
        zipcode: selectedBirthLocation.zipcode || '',
        country: selectedBirthLocation.country || { code: '', label: '' },
        state: selectedBirthLocation.state || { code: '', label: '' },
        city: selectedBirthLocation.city || {
          district: '',
          label: '',
          code: ''
        },
        geojson: selectedBirthLocation.geojson || { type: '', coordinates: null },
        iteration: 0
      };
    }

    // Add known languages if provided - use the stored complete objects directly
    if (selectedLanguagesData && selectedLanguagesData.length > 0) {
      payload.languages_known = selectedLanguagesData;
    } else if (formValues.knownLanguages && formValues.knownLanguages.length > 0) {
      // Fallback: try to process from form values (for backward compatibility)
      // Check if languages is loaded
      if (!languages || languages.length === 0) {
        // Skip processing if languages is not loaded
      } else {
        const languagesKnownData = formValues.knownLanguages.map(lang => {
          // Now lang should be an ID (string or number) - find the language by ID
          const languageId = typeof lang === 'string' ? lang : String(lang ?? '');
          
          const found = languages.find(language => language.id === languageId || String(language.id) === languageId);
          if (found && found.label) {
            return {
              label: found.label,
              code: found.code
            };
          }
          
          // Fallback: try to resolve as label/code (for backward compatibility)
          const langData = findMasterDataByValue(languages, lang);
          if (langData) {
            return {
              label: langData.label,
              code: langData.code
            };
          }
          
          // Final fallback: skip this language instead of storing invalid data
          return null;
        }).filter(Boolean); // Remove null entries

        // Send as languages_known array
        payload.languages_known = languagesKnownData;
      }
    }

    // Create family object with all family-related fields
    const familyData = {};

    // Add family name if provided
    if (formValues.familyName) {
      familyData.family_name = formValues.familyName;
    }

    // Add family type/status if provided
    if (formValues.familyStatus) {
      const familyTypeData = findMasterDataByValue(familyTypes, formValues.familyStatus);
      if (familyTypeData) {
        familyData.family_type = {
          code: familyTypeData.code,
          label: familyTypeData.label
        };
      }
    }

    // Add family location if provided - IMPORTANT: Only pass as a simple string, not an object
    if (formValues.familyLocation && formValues.familyLocation.trim() !== '') {
      // Family location is sent as a plain string value (e.g., "Kochi, Kerala")
      // NOT as a complex location object like the main location field
      familyData.family_location = String(formValues.familyLocation).trim();
    }

    // Add family description if provided
    if (formValues.familyDescription) {
      familyData.family_bio = formValues.familyDescription;
    }

    // Add father's information if provided
    if (formValues.fatherName) {
      familyData.father_name = formValues.fatherName;
    }
    if (formValues.fatherOccupation) {
      familyData.father_job = formValues.fatherOccupation;
    }
    if (formValues.fatherEducation) {
      familyData.father_education = formValues.fatherEducation;
    }
    // Add mother's information if provided
    if (formValues.motherName) {
      familyData.mother_name = formValues.motherName;
    }
    if (formValues.motherOccupation) {
      familyData.mother_job = formValues.motherOccupation;
    }
    if (formValues.motherEducation) {
      familyData.mother_education = formValues.motherEducation;
    }

    // Add siblings information
    if (formValues.brothersCount !== undefined && formValues.brothersCount !== null) {
      familyData.brothers_count = formValues.brothersCount;
    }
    if (formValues.sistersCount !== undefined && formValues.sistersCount !== null) {
      familyData.sisters_count = formValues.sistersCount;
    }

    // Add personal children count if provided (for divorced/widowed/separated individuals)
    // This should be inside the family object only, not at the root level
    if (formValues.noOfChildren !== undefined && formValues.noOfChildren !== null) {
      familyData.no_of_children = formValues.noOfChildren;
    }

    // Calculate total siblings count for generating siblings array
    const totalSiblings = (formValues.brothersCount || 0) + (formValues.sistersCount || 0);
    
    // Add siblings array structure if we have sibling information
    if (totalSiblings > 0) {
      const generatedSiblings = [];

      // Generate brother entries (is_elder should always be false per requirement)
      const numBrothers = Number(formValues.brothersCount || 0);
      for (let i = 0; i < numBrothers; i++) {
        generatedSiblings.push({
          is_elder: false,
          is_male: true,
          is_married: false,
          name: ''
        });
      }

      // Generate sister entries (is_elder should always be false per requirement)
      const numSisters = Number(formValues.sistersCount || 0);
      for (let i = 0; i < numSisters; i++) {
        generatedSiblings.push({
          is_elder: false,
          is_male: false,
          is_married: false,
          name: ''
        });
      }

      familyData.siblings = generatedSiblings;
    }

    // Note: family_location is already set above as a simple string label

    // Add family object to payload if it has any data
    if (Object.keys(familyData).length > 0) {
      payload.family = familyData;
    }

    // Add horoscope images if uploaded
    if (uploadedHoroscopeUrls && uploadedHoroscopeUrls.length > 0) {
      payload.horoscope_images = uploadedHoroscopeUrls.map((img, index) => ({
        original: img.s3Url,
        verification_status: 0 // Default status
      }));
    }

    // Add location coordinates if available (for main location, not family)
    if (selectedLocation && selectedLocation.lat && selectedLocation.lon) {
      payload.lat = selectedLocation.lat;
      payload.lon = selectedLocation.lon;
    }

    // Add zipcode if available from location
    if (selectedLocation && selectedLocation.zipcode) {
      payload.zipcode = selectedLocation.zipcode;
    }

    // Add country, state, city at root level for backward compatibility - always include if we have location data
    if (selectedLocation && selectedLocation.id && selectedLocation.id !== '') {
      payload.country = selectedLocation.country || { code: 'IN', label: 'India' };
      payload.state = selectedLocation.state || { code: '', label: '' };
      payload.geojson = selectedLocation.geojson || { type: 'Point', coordinates: [0, 0] };

      // Add county if available
      if (selectedLocation.county) {
        payload.county = selectedLocation.county;
      }

      // Always add display fields for main location (even if empty)
      payload.display_name = selectedLocation.display_name || '';
      payload.display_place = selectedLocation.display_place || '';
      payload.display_address = selectedLocation.display_address || '';
    }

    // Add default lookingFor text
    payload.looking_for = "Looking for a caring life partner who shares similar values and interests";

    return payload;
  }, [
    languages, maritalStatuses, religions, castes, heights, birthStars,
    jobs, educations, interests, selectedLocation, uploadedImageUrls,
    foodOptions, drinkingOptions, smokingOptions, ideologyOptions,
    interestsList, findMasterDataByValue, weightOptions, manglikOptions,
    uploadedHoroscopeUrls, familyTypes
  ]);


  // Note: parseTimeString function removed as we're now using CustomTimePicker

  // Helper function to determine if "No. of Children" field should be shown
  const shouldShowChildrenField = React.useCallback((maritalStatus, isChildrenFieldActive = false) => {
    // Show "No. of Children" field for Divorced status
    // Handle both string and integer values from API

    // Convert to string for comparison
    const statusStr = String(maritalStatus).toLowerCase().trim();

    // Check for various possible representations of statuses that require children count
    const isDivorced = maritalStatus === 'Divorced' ||
      maritalStatus === 'divorced' ||
      maritalStatus === 1 ||
      maritalStatus === '1' ||
      statusStr === 'divorced' ||
      statusStr === '1' ||
      statusStr.includes('divorced');

    const isWidowed = maritalStatus === 'Widowed' ||
      maritalStatus === 'widowed' ||
      maritalStatus === 2 ||
      maritalStatus === '2' ||
      statusStr === 'widowed' ||
      statusStr === '2' ||
      statusStr.includes('widowed');

    const isAwaitingDivorce = maritalStatus === 'Awaiting Divorce' ||
      maritalStatus === 'awaiting divorce' ||
      maritalStatus === 3 ||
      maritalStatus === '3' ||
      statusStr === '3' ||
      statusStr.includes('awaiting divorce');

    // Show field if Divorced, Widowed, or Awaiting Divorce
    const shouldShow = isDivorced || isWidowed || isAwaitingDivorce;

    return shouldShow;
  }, []);

  // Helper function to get marital status label from code
  const getMaritalStatusLabel = React.useCallback((code) => {
    if (!code) return '';

    // If we have marital statuses from API, find the label
    if (maritalStatuses && maritalStatuses.length > 0) {
      const status = maritalStatuses.find(s => s.code === code || s.label === code);
      return status ? status.label : code;
    }

    // Fallback for hardcoded values
    const statusMap = {
      'Single': 'Single',
      'Divorced': 'Divorced',
      'Widowed': 'Widowed',
      'Awaiting Divorce': 'Awaiting Divorce',
      'Separated': 'Separated',
      'Married': 'Married',
      '0': 'Single',           // Common code mapping
      '1': 'Divorced',         // Common code mapping
      '2': 'Widowed',          // Common code mapping
      '3': 'Awaiting Divorce', // Common code mapping
      '4': 'Separated',        // Common code mapping
      '5': 'Married'           // Common code mapping
    };

    return statusMap[code] || code;
  }, [maritalStatuses]);

  // Helper function to get marital status display with children count
  const getMaritalStatusDisplay = React.useCallback((maritalStatus, childrenCount) => {
    const statusLabel = getMaritalStatusLabel(maritalStatus);

    // For Divorced, Widowed, and Awaiting Divorce status, show children count if available
    if ((statusLabel === 'Divorced' || statusLabel === 'Widowed' || statusLabel === 'Awaiting Divorce') &&
      childrenCount !== undefined && childrenCount !== null && childrenCount > 0) {
      return `${statusLabel} (${childrenCount})`;
    }

    return statusLabel;
  }, [getMaritalStatusLabel]);

  // Handle marital status change - show/hide children field
  const handleMaritalStatusChange = React.useCallback((value) => {

    // Update marital status field
    handleFieldChange('maritalStatus', value);
    // Mark that a selection was made during this open session
    maritalSelectionMadeRef.current = true;

    // Check if the selected status is Divorced or Widowed
    const isDivorced = value === 'Divorced' ||
      value === 'divorced' ||
      value === 1 ||
      value === '1' ||
      String(value).toLowerCase().trim() === 'divorced' ||
      String(value).toLowerCase().trim() === '1' ||
      String(value).toLowerCase().trim().includes('divorced');

    const isWidowed = value === 'Widowed' ||
      value === 'widowed' ||
      value === 2 ||
      value === '2' ||
      String(value).toLowerCase().trim() === 'widowed' ||
      String(value).toLowerCase().trim() === '2' ||
      String(value).toLowerCase().trim().includes('widowed');

    const isAwaitingDivorce = value === 'Awaiting Divorce' ||
      value === 3 ||
      value === '3' ||
      String(value).toLowerCase().trim() === 'awaiting divorce' ||
      String(value).toLowerCase().trim() === '3' ||
      String(value).toLowerCase().trim().includes('awaiting divorce');

    if (isDivorced || isWidowed || isAwaitingDivorce) {
      // Activate children field but do not manipulate dropdown visibility
      setChildrenFieldActive(true);
      setMaritalCustomDropdownOpen(true);
      setChildrenDropdownOpen(true); // Open children dropdown so user can pick immediately
    } else {
      // Reset children field active state and count when switching away from Divorced status
      setChildrenFieldActive(false);
      handleFieldChange('noOfChildren', 0);
      form.setFieldValue('noOfChildren', 0);
      setMaritalCustomDropdownOpen(false); // Close marital dropdown for non-Divorced
      setChildrenDropdownOpen(false); // Close children dropdown
    }
  }, [handleFieldChange, form]);

  // Handle children count change - close popup after selection
  const handleChildrenCountChange = React.useCallback((value) => {

    // Update the children count field
    handleFieldChange('noOfChildren', value);
    form.setFieldValue('noOfChildren', value);

    // Close both dropdowns after children count is selected
    setChildrenDropdownOpen(false);
    setMaritalCustomDropdownOpen(false);

    // Keep children field active for future edits
    setChildrenFieldActive(true);
  }, [handleFieldChange]);

  // Handle children dropdown toggle
  const handleChildrenDropdownToggle = React.useCallback(() => {
    setChildrenDropdownOpen(!childrenDropdownOpen);
  }, [childrenDropdownOpen]);

  // Ref for debounce timeouts
  const languagesSearchTimeoutRef = useRef(null);
  const maritalStatusSearchTimeoutRef = useRef(null);
  const religionsSearchTimeoutRef = useRef(null);
  const castesSearchTimeoutRef = useRef(null);
  const heightsSearchTimeoutRef = useRef(null);
  const birthStarsSearchTimeoutRef = useRef(null);
  const jobsSearchTimeoutRef = useRef(null);
  const educationsSearchTimeoutRef = useRef(null);
  const interestsSearchTimeoutRef = useRef(null);
  const foodSearchTimeoutRef = useRef(null);
  const drinkingSearchTimeoutRef = useRef(null);
  const smokingSearchTimeoutRef = useRef(null);
  const ideologiesSearchTimeoutRef = useRef(null);
  const weightsSearchTimeoutRef = useRef(null);
  const manglikSearchTimeoutRef = useRef(null);
  const familyTypesSearchTimeoutRef = useRef(null);

  // Debounced search functions
  const debouncedSearchLanguages = React.useCallback((searchTerm) => {
    if (languagesSearchTimeoutRef.current) {
      clearTimeout(languagesSearchTimeoutRef.current);
    }
    languagesSearchTimeoutRef.current = setTimeout(() => {
      if (searchTerm && searchTerm.trim()) {
        searchLanguages(searchTerm);
      } else {
        // Reset list when input cleared or dropdown closed
        searchLanguages('');
      }
    }, 300);
  }, [searchLanguages]);

  const debouncedSearchMaritalStatuses = React.useCallback((searchTerm) => {
    if (maritalStatusSearchTimeoutRef.current) {
      clearTimeout(maritalStatusSearchTimeoutRef.current);
    }
    maritalStatusSearchTimeoutRef.current = setTimeout(() => {
      // Always call search function - with term if provided, empty string to reset if cleared
      searchMaritalStatuses(searchTerm && searchTerm.trim() ? searchTerm : '');
    }, 300);
  }, [searchMaritalStatuses]);

  const debouncedSearchReligions = React.useCallback((searchTerm) => {
    if (religionsSearchTimeoutRef.current) {
      clearTimeout(religionsSearchTimeoutRef.current);
    }
    religionsSearchTimeoutRef.current = setTimeout(() => {
      // Always call search function - with term if provided, empty string to reset if cleared
      searchReligions(searchTerm && searchTerm.trim() ? searchTerm : '');
    }, 300);
  }, [searchReligions]);

  const debouncedSearchCastes = React.useCallback((searchTerm) => {
    if (castesSearchTimeoutRef.current) {
      clearTimeout(castesSearchTimeoutRef.current);
    }
    castesSearchTimeoutRef.current = setTimeout(() => {
      // Always call search function - with term if provided, empty string to reset if cleared
      searchCastes(searchTerm && searchTerm.trim() ? searchTerm : '');
    }, 300);
  }, [searchCastes]);

  const debouncedSearchHeights = React.useCallback((searchTerm) => {
    if (heightsSearchTimeoutRef.current) {
      clearTimeout(heightsSearchTimeoutRef.current);
    }
    heightsSearchTimeoutRef.current = setTimeout(() => {
      // Always call search function - with term if provided, empty string to reset if cleared
      searchHeights(searchTerm && searchTerm.trim() ? searchTerm : '');
    }, 300);
  }, [searchHeights]);

  const debouncedSearchBirthStars = React.useCallback((searchTerm) => {
    if (birthStarsSearchTimeoutRef.current) {
      clearTimeout(birthStarsSearchTimeoutRef.current);
    }
    birthStarsSearchTimeoutRef.current = setTimeout(() => {
      // Always call search function - with term if provided, empty string to reset if cleared
      searchBirthStars(searchTerm && searchTerm.trim() ? searchTerm : '');
    }, 300);
  }, [searchBirthStars]);

  const debouncedSearchJobs = React.useCallback((searchTerm) => {
    if (jobsSearchTimeoutRef.current) {
      clearTimeout(jobsSearchTimeoutRef.current);
    }
    jobsSearchTimeoutRef.current = setTimeout(() => {
      // Always call search function - with term if provided, empty string to reset if cleared
      searchJobs(searchTerm && searchTerm.trim() ? searchTerm : '');
    }, 300);
  }, [searchJobs]);

  const debouncedSearchEducations = React.useCallback((searchTerm) => {
    if (educationsSearchTimeoutRef.current) {
      clearTimeout(educationsSearchTimeoutRef.current);
    }
    educationsSearchTimeoutRef.current = setTimeout(() => {
      // Always call search function - with term if provided, empty string to reset if cleared
      searchEducations(searchTerm && searchTerm.trim() ? searchTerm : '');
    }, 300);
  }, [searchEducations]);

  const debouncedSearchInterests = React.useCallback((searchTerm) => {
    if (interestsSearchTimeoutRef.current) {
      clearTimeout(interestsSearchTimeoutRef.current);
    }
    interestsSearchTimeoutRef.current = setTimeout(() => {
      // Always call search function - with term if provided, empty string to reset if cleared
      searchInterests(searchTerm && searchTerm.trim() ? searchTerm : '');
    }, 300);
  }, [searchInterests]);

  const debouncedSearchFood = React.useCallback((searchTerm) => {
    if (foodSearchTimeoutRef.current) {
      clearTimeout(foodSearchTimeoutRef.current);
    }
    foodSearchTimeoutRef.current = setTimeout(() => {
      // Always call search function - with term if provided, empty string to reset if cleared
      searchFood(searchTerm && searchTerm.trim() ? searchTerm : '');
    }, 300);
  }, [searchFood]);

  const debouncedSearchDrinking = React.useCallback((searchTerm) => {
    if (drinkingSearchTimeoutRef.current) {
      clearTimeout(drinkingSearchTimeoutRef.current);
    }
    drinkingSearchTimeoutRef.current = setTimeout(() => {
      // Always call search function - with term if provided, empty string to reset if cleared
      searchDrinking(searchTerm && searchTerm.trim() ? searchTerm : '');
    }, 300);
  }, [searchDrinking]);

  const debouncedSearchSmoking = React.useCallback((searchTerm) => {
    if (smokingSearchTimeoutRef.current) {
      clearTimeout(smokingSearchTimeoutRef.current);
    }
    smokingSearchTimeoutRef.current = setTimeout(() => {
      // Always call search function - with term if provided, empty string to reset if cleared
      searchSmoking(searchTerm && searchTerm.trim() ? searchTerm : '');
    }, 300);
  }, [searchSmoking]);

  const debouncedSearchIdeologies = React.useCallback((searchTerm) => {
    if (ideologiesSearchTimeoutRef.current) {
      clearTimeout(ideologiesSearchTimeoutRef.current);
    }
    ideologiesSearchTimeoutRef.current = setTimeout(() => {
      // Always call search function - with term if provided, empty string to reset if cleared
      searchIdeologies(searchTerm && searchTerm.trim() ? searchTerm : '');
    }, 300);
  }, [searchIdeologies]);

  const debouncedSearchWeights = React.useCallback((searchTerm) => {
    if (weightsSearchTimeoutRef.current) {
      clearTimeout(weightsSearchTimeoutRef.current);
    }
    weightsSearchTimeoutRef.current = setTimeout(() => {
      // Always call search function - with term if provided, empty string to reset if cleared
      searchWeights(searchTerm && searchTerm.trim() ? searchTerm : '');
    }, 300);
  }, [searchWeights]);

  const debouncedSearchManglikLists = React.useCallback((searchTerm) => {
    if (manglikSearchTimeoutRef.current) {
      clearTimeout(manglikSearchTimeoutRef.current);
    }
    manglikSearchTimeoutRef.current = setTimeout(() => {
      // Always call search function - with term if provided, empty string to reset if cleared
      searchManglikLists(searchTerm && searchTerm.trim() ? searchTerm : '');
    }, 300);
  }, [searchManglikLists]);

  const debouncedSearchFamilyTypes = React.useCallback((searchTerm) => {
    if (familyTypesSearchTimeoutRef.current) {
      clearTimeout(familyTypesSearchTimeoutRef.current);
    }
    familyTypesSearchTimeoutRef.current = setTimeout(() => {
      // Always call search function - with term if provided, empty string to reset if cleared
      searchFamilyTypes(searchTerm && searchTerm.trim() ? searchTerm : '');
    }, 300);
  }, [searchFamilyTypes]);

  // Handle marital status search
  const handleMaritalStatusSearch = React.useCallback((searchTerm) => {
    setMaritalStatusSearchTerm(searchTerm);
    debouncedSearchMaritalStatuses(searchTerm);
  }, [debouncedSearchMaritalStatuses]);

  // Handle marital status custom dropdown toggle
  const handleMaritalCustomDropdownToggle = React.useCallback(() => {
    const willOpen = !maritalCustomDropdownOpen;
    setMaritalCustomDropdownOpen(willOpen);
    // When opening, reset the selection-made marker
    if (willOpen) {
      maritalSelectionMadeRef.current = false;
    }
    // When closing parent dropdown, also close children dropdown and clear search
    if (!willOpen) {
      setMaritalStatusSearchTerm('');
      setChildrenDropdownOpen(false);
      // If the user closed without making a selection during this open session, reset values
      if (!maritalSelectionMadeRef.current) {
        handleFieldChange('maritalStatus', undefined);
        form.setFieldValue('maritalStatus', undefined);
        handleFieldChange('noOfChildren', 0);
        form.setFieldValue('noOfChildren', 0);
      }
    }
  }, [maritalCustomDropdownOpen]);

  // Unified click outside handler for both dropdowns
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      const isMaritalDropdownClick = maritalCustomDropdownRef.current && maritalCustomDropdownRef.current.contains(event.target);
      const isChildrenDropdownClick = childrenDropdownRef.current && childrenDropdownRef.current.contains(event.target);

      // If click is outside both dropdowns, close both
      if (!isMaritalDropdownClick && !isChildrenDropdownClick) {
        setMaritalCustomDropdownOpen(false);
        setChildrenDropdownOpen(false);
        // Clear search when closing dropdown
        setMaritalStatusSearchTerm('');
      }
    };

    // Add listener if either dropdown is open
    if (maritalCustomDropdownOpen || childrenDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [maritalCustomDropdownOpen, childrenDropdownOpen]);

  // Handle religion change - clear caste when religion changes
  const handleReligionChange = React.useCallback((value) => {
    // Update religion field
    handleFieldChange('religion', value);

    // Only clear caste selection when religion changes if we're NOT in prefilling mode
    if (!isPrefilling && formData.caste) {
      handleFieldChange('caste', '');
      form.setFieldValue('caste', ''); // Clear form field as well
      
      // Clear caste cache to prevent stale data
      if (clearCastesCache) {
        clearCastesCache();
      }
    }

    // Lock caste dropdown instantly and show loading until fresh list arrives
    setIsCasteRefreshing(true);
    casteLoadingReligionRef.current = value;
  }, [handleFieldChange, formData.caste, form, clearCastesCache, isPrefilling]);

  // Effect to refresh caste data when religion changes
  // During prefilling, we still need to refresh to get the correct caste list for the religion
  React.useEffect(() => {
    if (formData.religion && refreshCastes) {
      refreshCastes();
    }
  }, [formData.religion, refreshCastes, religionToDivisionCode]);

  // Unlock caste dropdown when new castes finish loading for the currently selected religion
  React.useEffect(() => {
    if (isCasteRefreshing) {
      if (!castesLoading) {
        // Castes finished loading; ensure the lock corresponds to the current religion
        if (casteLoadingReligionRef.current === formData.religion) {
          setIsCasteRefreshing(false);
        }
      }
    }
  }, [isCasteRefreshing, castesLoading, formData.religion]);

  // Effect to ensure caste prefilling happens when religion changes during prefilling
  // This handles the case where religion is set after castes have already loaded
  React.useEffect(() => {
    if (
      isPrefilling && 
      formData.religion && 
      pendingCasteDataRef.current && 
      castes && 
      castes.length > 0 && 
      !formData.caste &&
      !castesLoading &&
      (!pendingCasteDataRef.current.religionCode || pendingCasteDataRef.current.religionCode === formData.religion)
    ) {
      // Try multiple matching strategies
      let matchingCaste = castes.find(caste => 
        caste.code === pendingCasteDataRef.current.code
      );

      if (!matchingCaste) {
        matchingCaste = castes.find(caste => 
          caste.label === pendingCasteDataRef.current.label
        );
      }

      if (!matchingCaste) {
        matchingCaste = castes.find(caste => 
          caste.label?.toLowerCase() === pendingCasteDataRef.current.label?.toLowerCase()
        );
      }

      if (!matchingCaste && pendingCasteDataRef.current.id) {
        matchingCaste = castes.find(caste => 
          caste.id === pendingCasteDataRef.current.id
        );
      }
      
      if (matchingCaste) {
        form.setFieldValue('caste', matchingCaste.code);
        handleFieldChange('caste', matchingCaste.code);
        pendingCasteDataRef.current = null;
        
        // Clear the safety timeout if it exists
        if (prefillingTimeoutRef.current) {
          clearTimeout(prefillingTimeoutRef.current);
          prefillingTimeoutRef.current = null;
        }
        
        setTimeout(() => {
          setIsPrefilling(false);
        }, 500);
      }
    }
  }, [isPrefilling, formData.religion, formData.caste, castes, castesLoading, form, handleFieldChange]);




  // Check if bio generation button should be enabled
  const isBioGenerationEnabled = React.useCallback(() => {
    const requiredFields = {
      name: formData.fullName?.trim(),
      location: formData.location?.trim(),
      religion: formData.religion,
      caste: formData.caste,
      job: formData.job
    };

    // Check if all required fields are filled
    const allFieldsFilled = Object.values(requiredFields).every(field => {
      if (Array.isArray(field)) {
        return field.length > 0;
      }
      return field && field.toString().trim() !== '';
    });

    return allFieldsFilled;
  }, [formData]);

  // Regenerate bio function
  const regenerateBio = React.useCallback(async () => {
    if (!isBioGenerationEnabled()) {
      message.error('Please fill all required fields (Name, Location, Religion, Caste, Job) to generate bio.');
      return;
    }

    setBioRegenerateLoading(true);
    try {

      // Prepare payload with only the specified fields
      const bioGenerationPayload = {
        name: formData.fullName?.trim() || '',
        location: formData.location?.trim() || '',
        religion: findMasterDataByValue(religions, formData.religion)?.label || formData.religion || '',
        caste: findMasterDataByValue(castes, formData.caste)?.label || formData.caste || '',
        job: findMasterDataByValue(jobs, formData.job)?.label || formData.job || '',
        lookingFor: "Looking for a caring life partner who shares similar values and interests",
        interests: interests && interests.length > 0 ? interests.map(interest => interest.label || interest).join(', ') : ''
      };

      const response = await axios.post(API_ENDPOINTS.BIO.GENERATE, bioGenerationPayload);

      if (response.success && response.data?.bio) {
        const generatedBio = response.data.bio;

        // Update both form field and formData state
        form.setFieldValue('bio', generatedBio);
        handleFieldChange('bio', generatedBio);

        message.success('Bio generated successfully!');
      } else {
        throw new Error(response.message || 'Failed to generate bio');
      }

    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to generate bio. Please try again.';
      message.error(errorMessage);
    } finally {
      setBioRegenerateLoading(false);
    }
  }, [formData, interests, form, handleFieldChange, isBioGenerationEnabled, findMasterDataByValue, religions, castes, jobs]);

  // Form submission
  const onFinish = React.useCallback(async (values) => {

    // First verify basic details; surface missing fields if any
    const basicOk = validateBasicDetails();
    if (!basicOk) {
      try {
        await form.validateFields();
      } catch (e) {
        // handled in onFinishFailed
      }
      return;
    }

    if (!validateImages()) {
      return;
    }

    setSubmissionLoading(true);
    try {
      // Transform form data to API payload structure
      // Use formData state instead of values to ensure all prefilled data is included
      const transformedPayload = transformFormDataToPayload(formData);



      let response;
      let profileId;

      if (isEditMode && currentProfileId) {
        // Edit existing profile
        const updateUrl = API_ENDPOINTS.PROFILE.UPDATE(currentProfileId);

        response = await axios.put(updateUrl, transformedPayload);
        profileId = currentProfileId;
      } else {
        // Create new profile

        response = await axios.post(API_ENDPOINTS.PROFILE.CREATE, transformedPayload);

        // Extract profile ID from response - check various possible locations
        profileId = response.data?.id || response.data?._id || response.data?.profileId || response.id || response._id;
      }

      if (response.success) {
        const successMessage = isEditMode ? 'Profile updated successfully!' : 'Profile created successfully!';
        // message.success(successMessage);

        // Set the profile ID and fetch complete profile data from API
        setCurrentProfileId(profileId);

        // Fetch the complete profile data from API to show in ProfileDetailsView
        if (profileId) {
          await fetchProfileDetails(profileId);
        } else {
          // Fallback: if no profileId, show error
          message.error('Profile ID not returned from server. Please try again.');
        }

      } else {
        throw new Error(response.message || `Failed to ${isEditMode ? 'update' : 'create'} profile`);
      }

    } catch (error) {
      // Show user-friendly error message
      const errorMessage = error.response?.data?.message || error.message || `Failed to ${isEditMode ? 'update' : 'create'} profile. Please try again.`;
      message.error(errorMessage);

    } finally {
      setSubmissionLoading(false);
    }
  }, [validateImages, transformFormDataToPayload, fetchProfileDetails, isEditMode, currentProfileId, form, validateBasicDetails, formData, selectedInterestsData, selectedLanguagesData]);

  const onFinishFailed = React.useCallback((errorInfo) => {
    try {
      const fieldLabelMap = {
        fullName: 'Name',
        motherTongue: 'Mother Tongue',
        gender: 'Gender',
        dob: 'DOB',
        maritalStatus: 'Marital Status',
        religion: 'Religion',
        caste: 'Caste',
        height: 'Height',
        isDisabled: 'Disabled',
        location: 'Location',
        job: 'Job',
        education: 'Education',
        bio: 'Bio'
      };

      const missing = (errorInfo?.errorFields || [])
        .map(f => (Array.isArray(f.name) ? f.name[0] : f.name))
        .filter(Boolean)
        .map(key => fieldLabelMap[key] || key);

      // Check for image validation error
      const isImageValidationError = selectedImages.length === 0 || !imagesUploaded;
      if (isImageValidationError) {
        setImageValidationError(true);
        missing.push('Upload Photo(s)');
      } else {
        setImageValidationError(false);
      }

      if (missing.length > 0) {
        message.error(`Please fill required fields: ${missing.join(', ')}`);
        // Scroll to the first missing field
        const first = errorInfo.errorFields[0]?.name;
        if (first) {
          form.scrollToField(first);
        }
      }
    } catch (e) {
      // no-op
    }
  }, [form, selectedImages, imagesUploaded]);
useEffect(()=>{
},[formData])
  // Generate height options - use API data with cm/ft labels
  const generateHeightOptions = React.useCallback(() => {
    if (heights && heights.length > 0) {
      return heights.map(height => ({
        label: height.label || height.name || `${height.code} cm`,
        value: height.code || height.id || height.value
      }));
    }

    return [];
  }, [heights]);

  const showModal = React.useCallback(() => {
  }, []);

  // Handle back to form
  const handleBackToForm = React.useCallback(async () => {
    if (currentProfileId) {
      // Navigate to profile-creation route and fetch profile data for editing
      navigate(`/profile-creation/${currentProfileId}`);
      setShowDetailsView(false);
      setSubmittedProfileData(null);
      await fetchProfileForEdit(currentProfileId);
    } else {
      // Just go back to the form without any data
      navigate('/profile-creation');
      setShowDetailsView(false);
      setSubmittedProfileData(null);
    }
  }, [currentProfileId, fetchProfileForEdit, navigate]);

  // Handle profile confirmation
  const handleProfileConfirm = React.useCallback(async () => {
    if (!currentProfileId) {
      message.error('Profile ID not found. Cannot confirm profile.');
      return;
    }

    setSubmissionLoading(true);
    try {

      // Call confirm API to set status to Active
      const response = await axios.put(API_ENDPOINTS.PROFILE.CONFIRM(currentProfileId));

      if (response.success) {
        message.success('Profile confirmed and activated successfully!');

        // Update the profile data to reflect the new status
        if (submittedProfileData) {
          setSubmittedProfileData(prev => ({
            ...prev,
            status: 'active'
          }));
        }

        // Optionally redirect to dashboard or success page
        setTimeout(() => {
          navigate('/my-profiles');
        }, 2000);

      } else {
        throw new Error(response.message || 'Failed to confirm profile');
      }

    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to confirm profile. Please try again.';
      message.error(errorMessage);
    } finally {
      setSubmissionLoading(false);
    }
  }, [currentProfileId, submittedProfileData, navigate]);







  // Show details view if profile was submitted or if we're in details mode
  if (showDetailsView) {
    return (
      <ProfileDetailsView  
        className={'right_aligned'}
        profileData={submittedProfileData}
        onBack={handleBackToForm}
        onConfirm={handleProfileConfirm}
        loading={profileLoading || submissionLoading}
      />
    );
  }

  // Show loading spinner while fetching profile data
  if (profileLoading && !showDetailsView) {
    return (
      <div className="profile_wrap">
        <Card className="profile_creation_card">
          <div className="spinner sm center" style={{ padding: '50px', textAlign: 'center' }}>
            <Spin size="large" />
            <p style={{ marginTop: '20px', fontSize: '16px', color: '#666' }}>
              {isEditMode ? 'Loading profile data...' : 'Loading...'}
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="profile_wrap">


        <div className="top_wrap show_preview preview_btn">
          <button className='sticky_button mobile_preview' onClick={showPreviewModal}>
            <Tooltip title="Mobile Preview" placement="top">
              <img src={previewIcon} alt="Preview" />
            </Tooltip>
          </button>
          {/* <Link onClick={showModal}>
            Show Preview
          </Link> */}
        </div>
        <Card className="profile_creation_card">

          {submissionLoading && (
            <div className="spinner sm center">
              <Spin />
            </div>
          )}

          <div className="profile_creation_form form_wrap">
            <Form
              form={form}
              className="form_body personal_details form_wrap"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              layout="vertical"
              initialValues={formData}
            >
              <Collapse
                defaultActiveKey={['basicDetails']}
                className="profile_accordion"
                items={[
                  {
                    key: 'basicDetails',
                    label: 'Basic Information',
                    children: (
                      <div className="accordion_content basic_information">
                        {/* Basic Details Fields */}
                        <div className="input_holder w_half">
                          <label htmlFor="fullName" className=" d-block input_label">
                            Name <span className="text_red">*</span>
                          </label>
                          <Form.Item
                            name="fullName"
                            rules={[{ required: true, message: content?.EnterFullName }]}
                          >
                            <Input
                              className="input_field"
                              placeholder={placeHolderContent?.FullName}
                              type="text"
                              maxLength={fieldLimits.fullName}
                              onChange={(e) => handleFieldChange('fullName', e.target.value)}
                            />
                          </Form.Item>
                        </div>

                        <div className="input_holder">
                          <label htmlFor="motherTongue" className=" d-block input_label">
                            Mother Tongue <span className="text_red">*</span>
                          </label>
                          <Form.Item
                            name="motherTongue"
                            rules={[{ required: true, message: content?.EnterMotherTongue }]}
                          >
                            <Select
                              className="input_field creation_dropdown"
                              placeholder={placeHolderContent?.MotherTongue}
                              onChange={(value) => handleFieldChange('motherTongue', value)}
                              loading={languagesLoading}
                              onPopupScroll={handleLanguagesScroll}
                              showSearch
                              filterOption={false}
                              suffixIcon={<img src={dropdownIcon} alt="" style={{ width: '14px ' , height: '14px' , objectFit: 'contain'}} />}
                              onSearch={(value) => {
                                debouncedSearchLanguages(value);
                              }}
                              dropdownRender={(menu) => (
                                <div>
                                  {menu}
                                  {languagesLoadingMore && (
                                    <div style={{
                                      textAlign: 'center',
                                      padding: '8px',
                                      borderTop: '1px solid #f0f0f0',
                                      background: '#fafafa'
                                    }}>
                                      <Spin size="small" /> Loading more languages...
                                    </div>
                                  )}
                                </div>
                              )}
                              notFoundContent={
                                languagesLoading ? (
                                  <div style={{ textAlign: 'center', padding: '8px' }}>
                                    <Spin size="small" /> Loading languages...
                                  </div>
                                ) : languagesError ? (
                                  <div style={{ textAlign: 'center', padding: '8px', color: '#ff4d4f' }}>
                                    Error loading languages: {languagesError}
                                  </div>
                                ) : (
                                  <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
                                    No languages found
                                  </div>
                                )
                              }
                            >
                              {languages && languages.length > 0 && (
                                languages.map((language, index) => {
                                  return (
                                    <Option key={language.id || language.code || index} value={language.code || language.label || language.name}>
                                      {language.name || language.label || language.code}
                                    </Option>
                                  );
                                })
                              )}
                            </Select>
                          </Form.Item>
                        </div>

                        <div className="input_holder">
                          <label className=" d-block input_label">
                            Gender <span className="text_red">*</span>
                          </label>
                          <Form.Item
                            name="gender"
                            rules={[{ required: true, message: content?.SelectGender }]}
                          >
                            <Select
                              className="input_field creation_dropdown"
                              placeholder={placeHolderContent?.Gender}
                              onChange={(value) => handleFieldChange('gender', value)}
                              suffixIcon={<img src={dropdownIcon} alt="" style={{ width: '14px ' , height: '14px' , objectFit: 'contain'}} />}
                            >
                              <Option value="Male">Male</Option>
                              <Option value="Female">Female</Option>
                            </Select>
                          </Form.Item>
                        </div>

                        <div className="input_holder age_field">
                          <label htmlFor="dob" className=" d-block input_label">
                            DOB (MM-DD-YY) <span className="text_red">*</span>
                            {formData.gender && (
                              <span className="min_age" style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginLeft: '8px' }}>
                                (Min age: {formData.gender === 'Male' ? '21' : '18'} years)
                              </span>
                            )}
                          </label>
                          <Form.Item
                            name="dob"
                            rules={[
                              { required: true, message: content?.EnterDOB },
                              {
                                validator: (_, value) => {
                                  if (!value || !formData.gender) return Promise.resolve();
                                  
                                  const selectedDate = new Date(value);
                                  const maxAllowedDate = getMaxAllowedDOB(formData.gender);
                                  
                                  if (selectedDate > maxAllowedDate) {
                                    const minAge = formData.gender === 'Male' ? 21 : 18;
                                    return Promise.reject(
                                      new Error(`${formData.gender === 'Male' ? 'Males' : 'Females'} must be at least ${minAge} years old`)
                                    );
                                  }
                                  
                                  return Promise.resolve();
                                }
                              }
                            ]}
                          >
                            <CustomDatePicker
                              value={formData.dob}
                              onChange={(date) => handleFieldChange('dob', date)}
                              required={true}
                              placeholder={placeHolderContent?.DOB || "Select DOB"}
                              className="input_field date_picker_custom"
                              maxDate={formData.gender ? getMaxAllowedDOB(formData.gender) : null}
                              gender={formData.gender}
                            />
                          </Form.Item>
                        </div>

                        <div className="marital_status_wrap input_holder">
                          <div className="input_holder marital_status">
                            <label htmlFor="maritalStatus" className=" d-block input_label">
                              Marital Status <span className="text_red">*</span>
                            </label>
                            <Form.Item
                              name="maritalStatus"
                              rules={[{ required: true, message: content?.SelectMaritalStatus }]}
                            >
                              <div ref={maritalCustomDropdownRef} className="custom-dropdown-wrapper" style={{ position: 'relative', width: '100%' }}>
                                <div
                                  className={`input_field custom-dropdown-trigger ${maritalCustomDropdownOpen ? 'dropdown-open' : ''}`}
                                  onClick={handleMaritalCustomDropdownToggle}
                                  style={{
                                  }}
                                >
                                  <span 
                                    className={`marital_status_label ${!formData.maritalStatus ? 'placeholder-text' : ''}`}
                                    style={{
                                      color: formData.maritalStatus ? '#000000d9' : undefined
                                    }}
                                  >
                                    {formData.maritalStatus 
                                      ? getMaritalStatusDisplay(formData.maritalStatus, formData.noOfChildren)
                                      : (placeHolderContent?.MaritalStatus || 'Select Marital Status')
                                    }
                                  </span>
                                  <span style={{
                                   display: 'flex',
                                   alignItems: 'center',
                                   justifyContent: 'center',
                                  }}>
                                    <img src={dropdownIcon} alt="" />
                                  </span>
                                </div>

                                {maritalCustomDropdownOpen && (
                                  <div
                                    className="custom-dropdown-menu"
                                    style={{
                                      position: 'absolute',
                                      top: '100%',
                                      left: 0,
                                      right: 0,
                                      zIndex: 1050,
                                      backgroundColor: '#fff',
                                      border: '1px solid #d9d9d9',
                                      borderRadius: '6px',
                                      boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
                                      maxHeight: '256px',
                                      overflow: 'auto',
                                      marginTop: '4px'
                                    }}
                                    onScroll={handleMaritalStatusesScroll}
                                  >
                                    {/* Search Input - Show only when total count > 10 */}
                                    {maritalStatusesTotalCount > 10 && (
                                      <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                                        <Input
                                          placeholder="Search marital status..."
                                          value={maritalStatusSearchTerm}
                                          onChange={(e) => handleMaritalStatusSearch(e.target.value)}
                                          style={{ fontSize: '14px' }}
                                          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                        />
                                      </div>
                                    )}
                                    {maritalStatusesLoading ? (
                                      <div style={{ textAlign: 'center', padding: '8px' }}>
                                        <Spin size="small" /> Loading marital statuses...
                                      </div>
                                    ) : maritalStatuses && maritalStatuses.length > 0 ? (
                                      maritalStatuses.map(status => (
                                        <div
                                          key={status.id}
                                          className="custom-dropdown-option ant-select-item"
                                          onClick={() => handleMaritalStatusChange(status.code || status.label)}
                                          style={{
                                            padding: '5px 12px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            lineHeight: '22px',
                                            color: '#000000d9',
                                            backgroundColor: formData.maritalStatus === (status.code || status.label) ? '#e6f7ff' : 'transparent',
                                            borderBottom: '1px solid #f0f0f0'
                                          }}
                                          onMouseEnter={(e) => {
                                            if (formData.maritalStatus !== (status.code || status.label)) {
                                              e.target.style.backgroundColor = '#f5f5f5';
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            if (formData.maritalStatus !== (status.code || status.label)) {
                                              e.target.style.backgroundColor = 'transparent';
                                            }
                                          }}
                                        >
                                          {status.label}
                                        </div>
                                      ))
                                    ) : null}

                                    {/* Loading more indicator */}
                                    {maritalStatusesLoadingMore && (
                                      <div style={{
                                        textAlign: 'center',
                                        padding: '8px',
                                        borderTop: '1px solid #f0f0f0',
                                        background: '#fafafa'
                                      }}>
                                        <Spin size="small" /> Loading more marital statuses...
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </Form.Item>
                          </div>

                          {/* Conditional "No. of Children" field - Show alongside marital dropdown when Divorced */}
                          {(() => {
                            const shouldShow = shouldShowChildrenField(formData.maritalStatus, childrenFieldActive);
                            return shouldShow;
                          })() && (
                              <div className={`input_holder no_of_children ${childrenDropdownOpen ? 'children-dropdown-open' : ''}`} style={{ marginTop: '10px' }}>
                                <label htmlFor="noOfChildren" className="d-block input_label">
                                  No. of Children <span className="text_red">*</span>
                                </label>
                                <Form.Item
                                  name="noOfChildren"
                                  rules={[{ required: true, message: 'Please select number of children' }]}
                                >
                                  <div ref={childrenDropdownRef} className="custom-dropdown-wrapper" style={{ position: 'relative', width: '120px' }}>
                                    <div
                                      className={`input_field custom-dropdown-trigger ${childrenDropdownOpen ? 'dropdown-open' : ''}`}
                                      onClick={handleChildrenDropdownToggle}
                                      style={{
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '4px 11px',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: '6px',
                                        backgroundColor: '#fff',
                                        minHeight: '32px',
                                        fontSize: '14px',
                                        lineHeight: '1.5715',
                                        color: formData.noOfChildren !== undefined && formData.noOfChildren !== null && formData.noOfChildren !== '' ? '#000000d9' : '#bfbfbf'
                                      }}
                                    >
                                      <span>
                                        {formData.noOfChildren !== undefined && formData.noOfChildren !== null && formData.noOfChildren !== ''
                                          ? formData.noOfChildren
                                          : 'Select number of children'
                                        }
                                      </span>
                                      <span style={{
                                        transform: childrenDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.3s',
                                        fontSize: '12px',
                                        color: '#bfbfbf'
                                      }}>
                                        
                                      </span>
                                    </div>

                                    {childrenDropdownOpen && (
                                      <div
                                        className="custom-dropdown-menu"
                                        style={{
                                          position: 'absolute',
                                          top: '100%',
                                          left: 0,
                                          right: 0,
                                          zIndex: 1051, // Higher z-index than marital dropdown
                                          backgroundColor: '#fff',
                                          border: '1px solid #d9d9d9',
                                          borderRadius: '6px',
                                          boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
                                          maxHeight: '256px',
                                          overflow: 'auto',
                                          marginTop: '4px'
                                        }}
                                      >
                                        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                                          <div
                                            key={num}
                                            className="custom-dropdown-option"
                                            onClick={() => handleChildrenCountChange(num)}
                                            style={{
                                              padding: '5px 12px',
                                              cursor: 'pointer',
                                              fontSize: '14px',
                                              lineHeight: '22px',
                                              color: '#000000d9',
                                              backgroundColor: formData.noOfChildren === num ? '#e6f7ff' : 'transparent',
                                              borderBottom: num < 10 ? '1px solid #f0f0f0' : 'none'
                                            }}
                                            onMouseEnter={(e) => {
                                              if (formData.noOfChildren !== num) {
                                                e.target.style.backgroundColor = '#f5f5f5';
                                              }
                                            }}
                                            onMouseLeave={(e) => {
                                              if (formData.noOfChildren !== num) {
                                                e.target.style.backgroundColor = 'transparent';
                                              }
                                            }}
                                          >
                                            {num}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </Form.Item>
                              </div>
                            )}
                        </div>

                        <div className="input_holder ">
                          <label htmlFor="height" className=" d-block input_label">
                            Height <span className="text_red">*</span>
                          </label>
                          <Form.Item
                            name="height"
                            rules={[{ required: true, message: content?.SelectHeight }]}
                          >
                            <Select
                              className="input_field creation_dropdown"
                              placeholder={placeHolderContent?.Height}
                                onChange={(value, option) => {
                                  // Store the complete masterdata object if available
                                  const selectedHeight = heights.find(height => height.code === value || height.label === value);
                                  if (selectedHeight) {
                                    handleFieldChange('height', selectedHeight);
                                  } else {
                                    handleFieldChange('height', value);
                                  }
                                }}
                              loading={heightsLoading}
                              onPopupScroll={handleHeightsScroll}
                              showSearch
                              filterOption={false}
                              suffixIcon={<img src={dropdownIcon} alt="" style={{ width: '14px ' , height: '14px' , objectFit: 'contain'}} />}
                              onSearch={(value) => {
                                debouncedSearchHeights(value);
                              }}
                              onDropdownVisibleChange={(open) => {
                                // Heights dropdown visibility debug removed
                              }}
                              dropdownRender={(menu) => (
                                <div>
                                  {menu}
                                  {heightsLoadingMore && (
                                    <div style={{
                                      textAlign: 'center',
                                      padding: '8px',
                                      borderTop: '1px solid #f0f0f0',
                                      background: '#fafafa'
                                    }}>
                                      <Spin size="small" /> Loading more heights...
                                    </div>
                                  )}
                                </div>
                              )}
                              notFoundContent={
                                heightsLoading ? (
                                  <div style={{ textAlign: 'center', padding: '8px' }}>
                                    <Spin size="small" /> Loading heights...
                                  </div>
                                ) : (
                                  <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
                                    No heights found
                                  </div>
                                )
                              }
                            >
                              {generateHeightOptions().map(height => (
                                <Option key={height.value} value={height.value}>
                                  {height.label}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </div>

                        <div className="input_holder ">
                          <label className=" d-block input_label">
                            Disabled <span className="text_red">*</span>
                          </label>
                          <Form.Item
                            name="isDisabled"
                            rules={[{ required: true, message: content?.SelectDisabilityStatus }]}
                          >
                            <Select
                              className="input_field creation_dropdown"
                              placeholder={placeHolderContent?.DisabilityStatus}
                              onChange={(value) => handleFieldChange('isDisabled', value)}
                              suffixIcon={<img src={dropdownIcon} alt="" style={{ width: '14px ' , height: '14px' , objectFit: 'contain'}} />}
                            >
                              <Option value="Yes">Yes</Option>
                              <Option value="No">No</Option>
                            </Select>
                          </Form.Item>
                        </div>

                        <div className="input_holder w_full">
                          <label htmlFor="location" className=" d-block input_label">
                            Location <span className="text_red">*</span>
                          </label>
                          <Form.Item
                            name="location"
                            rules={[{ required: true, message: content?.SelectLocation }]}
                          >
                            <LocationSelection
                              apiUrl={API_ENDPOINTS.LOCATION.PLACES}
                              placeholder={placeHolderContent?.Location}
                              selectedLocation={handleMainLocationSelection}
                              value={formData.location}
                              externalLoading={
                                languagesLoading || religionsLoading || castesLoading || heightsLoading ||
                                jobsLoading || educationsLoading || birthStarsLoading || weightLoading ||
                                interestsLoading || foodLoading || drinkingLoading || smokingLoading ||
                                ideologyLoading || manglikLoading
                              }
                              errorHandle={(hasError) => {
                              }}
                              disableStatus={false}
                              debug={true}
                            />
                          </Form.Item>
                        </div>

                        <div className="input_holder ">
                          <label htmlFor="job" className=" d-block input_label">
                            Job <span className="text_red">*</span>
                          </label>
                          <Form.Item
                            name="job"
                            rules={[{ required: true, message: content?.SelectJob }]}
                          >
                            <Select
                              className="input_field creation_dropdown"
                              placeholder={placeHolderContent?.Job}
                                onChange={(value, option) => {
                                  // Store the complete masterdata object if available
                                  const selectedJob = jobs.find(job => job.code === value || job.label === value);
                                  if (selectedJob) {
                                    handleFieldChange('job', selectedJob);
                                  } else {
                                    handleFieldChange('job', value);
                                  }
                                }}
                              loading={jobsLoading}
                              onPopupScroll={handleJobsScroll}
                              showSearch
                              filterOption={false}
                              suffixIcon={<img src={dropdownIcon} alt="" style={{ width: '14px ' , height: '14px' , objectFit: 'contain'}} />}
                              onSearch={(value) => {
                                debouncedSearchJobs(value);
                              }}
                              dropdownRender={(menu) => (
                                <div>
                                  {menu}
                                  {jobsLoadingMore && (
                                    <div style={{
                                      textAlign: 'center',
                                      padding: '8px',
                                      borderTop: '1px solid #f0f0f0',
                                      background: '#fafafa'
                                    }}>
                                      <Spin size="small" /> Loading more jobs...
                                    </div>
                                  )}
                                </div>
                              )}
                              notFoundContent={
                                jobsLoading ? (
                                  <div style={{ textAlign: 'center', padding: '8px' }}>
                                    <Spin size="small" /> Loading jobs...
                                  </div>
                                ) : (
                                  <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
                                    No jobs found
                                  </div>
                                )
                              }
                            >
                              {jobs && jobs.length > 0 &&
                                jobs.map(job => (
                                  <Option key={job.id} value={job.code || job.label}>
                                    {job.label}
                                  </Option>
                                ))
                              }
                            </Select>
                          </Form.Item>
                        </div>

                        <div className="input_holder ">
                          <label htmlFor="education" className=" d-block input_label">
                            Education <span className="text_red">*</span>
                          </label>
                          <Form.Item
                            name="education"
                            rules={[{ required: true, message: content?.SelectEducation }]}
                          >
                            <Select
                              className="input_field creation_dropdown"
                              placeholder={placeHolderContent?.Education}
                                onChange={(value, option) => {
                                  // Store the complete masterdata object if available
                                  const selectedEducation = educations.find(edu => edu.code === value || edu.label === value);
                                  if (selectedEducation) {
                                    handleFieldChange('education', selectedEducation);
                                  } else {
                                    handleFieldChange('education', value);
                                  }
                                }}
                              loading={educationsLoading}
                              onPopupScroll={handleEducationsScroll}
                              showSearch
                              filterOption={false}
                              suffixIcon={<img src={dropdownIcon} alt="" style={{ width: '14px ' , height: '14px' , objectFit: 'contain'}} />}
                              onSearch={(value) => {
                                debouncedSearchEducations(value);
                              }}
                              dropdownRender={(menu) => (
                                <div>
                                  {menu}
                                  {educationsLoadingMore && (
                                    <div style={{
                                      textAlign: 'center',
                                      padding: '8px',
                                      borderTop: '1px solid #f0f0f0',
                                      background: '#fafafa'
                                    }}>
                                      <Spin size="small" /> Loading more educations...
                                    </div>
                                  )}
                                </div>
                              )}
                              notFoundContent={
                                educationsLoading ? (
                                  <div style={{ textAlign: 'center', padding: '8px' }}>
                                    <Spin size="small" /> Loading educations...
                                  </div>
                                ) : (
                                  <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
                                    No educations found
                                  </div>
                                )
                              }
                            >
                              {educations && educations.length > 0 &&
                                educations.map(education => (
                                  <Option key={education.id} value={education.code || education.label}>
                                    {education.label}
                                  </Option>
                                ))
                              }
                            </Select>
                          </Form.Item>
                        </div>

                        <div className="input_holder ">
                          <label htmlFor="religion" className=" d-block input_label">
                            Religion <span className="text_red">*</span>
                          </label>
                          <Form.Item
                            name="religion"
                            rules={[{ required: true, message: content?.SelectReligion }]}
                          >
                            <Select
                              className="input_field creation_dropdown"
                              placeholder={placeHolderContent?.Religion}
                              onChange={handleReligionChange}
                              loading={religionsLoading}
                              onPopupScroll={handleReligionsScroll}
                              showSearch
                              filterOption={false}
                              suffixIcon={<img src={dropdownIcon} alt="" style={{ width: '14px ' , height: '14px' , objectFit: 'contain'}} />}
                              onSearch={(value) => {
                                debouncedSearchReligions(value);
                              }}
                              dropdownRender={(menu) => (
                                <div>
                                  {menu}
                                  {religionsLoadingMore && (
                                    <div style={{
                                      textAlign: 'center',
                                      padding: '8px',
                                      borderTop: '1px solid #f0f0f0',
                                      background: '#fafafa'
                                    }}>
                                      <Spin size="small" /> Loading more religions...
                                    </div>
                                  )}
                                </div>
                              )}
                              notFoundContent={
                                religionsLoading ? (
                                  <div style={{ textAlign: 'center', padding: '8px' }}>
                                    <Spin size="small" /> Loading religions...
                                  </div>
                                ) : religionsError ? (
                                  <div style={{ textAlign: 'center', padding: '8px', color: '#ff4d4f' }}>
                                    Error loading religions: {religionsError}
                                  </div>
                                ) : (
                                  <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
                                    No religions found
                                  </div>
                                )
                              }
                            >
                              {religions && religions.length > 0 && (
                                religions.map((religion, index) => {
                                  return (
                                    <Option key={religion.id || religion.code || index} value={religion.code || religion.label || religion.name}>
                                      {religion.label || religion.name || religion.code}
                                    </Option>
                                  );
                                })
                              )}
                            </Select>
                          </Form.Item>
                        </div>

                        <div className="input_holder">
                          <label htmlFor="caste" className=" d-block input_label">
                            Caste <span className="text_red">*</span>
                          </label>
                          <Form.Item
                            name="caste"
                            rules={[{ required: true, message: content?.SelectCaste }]}
                          >
                            <Select
                              className="input_field creation_dropdown"
                              placeholder={
                                !formData.religion
                                  ? "Please select religion first"
                                  : placeHolderContent?.Caste
                              }
                                onChange={(value, option) => {
                                  // Store the complete masterdata object if available
                                  const selectedCaste = castes.find(caste => caste.code === value || caste.id === value);
                                  if (selectedCaste) {
                                    handleFieldChange('caste', selectedCaste);
                                  } else {
                                    handleFieldChange('caste', value);
                                  }
                                }}
                              loading={castesLoading || isCasteRefreshing}
                              disabled={!formData.religion || isCasteRefreshing}
                              onPopupScroll={handleCastesScroll}
                              showSearch
                              filterOption={false}
                              suffixIcon={<img src={dropdownIcon} alt="" style={{ width: '14px ' , height: '14px' , objectFit: 'contain'}} />}
                              onSearch={(value) => {
                                debouncedSearchCastes(value);
                              }}
                              dropdownRender={(menu) => (
                                <div>
                                  {/* Hide existing menu while refreshing to avoid stale selections */}
                                  {isCasteRefreshing ? (
                                    <div style={{ textAlign: 'center', padding: '8px' }}>
                                      <Spin size="small" /> Loading castes...
                                    </div>
                                  ) : (
                                    menu
                                  )}
                                  {castesLoadingMore && (
                                    <div style={{
                                      textAlign: 'center',
                                      padding: '8px',
                                      borderTop: '1px solid #f0f0f0',
                                      background: '#fafafa'
                                    }}>
                                      <Spin size="small" /> Loading more castes...
                                    </div>
                                  )}
                                </div>
                              )}
                              notFoundContent={
                                (castesLoading || isCasteRefreshing) ? (
                                  <div style={{ textAlign: 'center', padding: '8px' }}>
                                    <Spin size="small" /> Loading castes...
                                  </div>
                                ) : !formData.religion ? (
                                  <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
                                    Please select a religion first
                                  </div>
                                ) : (
                                  <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
                                    No castes found for {formData.religion}
                                  </div>
                                )
                              }
                            >
                              {!isCasteRefreshing && castes && castes.length > 0 &&
                                castes.map(caste => {
                                  const truncatedLabel = caste.label && caste.label.length > 30
                                    ? `${caste.label.substring(0, 30)}...`
                                    : caste.label;

                                  return (
                                    <Option key={caste.id} value={caste.code || caste.id} title={caste.label}>
                                      {truncatedLabel}
                                    </Option>
                                  );
                                })
                              }
                            </Select>
                          </Form.Item>
                        </div>

                        {/* Image Upload Section */}
                        <div className="image_upload_section  input_holder w_full">
                          <label className="d-block input_label">
                            Upload Photo(s)  <span className="text_red">*</span>
                          </label>
                          <div className="image_selector_wrapper ">
                            <ImageSelector
                              value={selectedImages}
                              onChange={handleImageChange}
                              maxImages={5}
                              maxFileSize={5 * 1024 * 1024}
                              acceptedTypes={['image/jpeg', 'image/png', 'image/gif', 'image/webp']}
                              aspectRatio={1}
                              minDimensions={{ width: 150, height: 150 }}
                            />

                            {/* Upload Button */}
                            {showUploadButton && (
                              <div className="upload_button_container" style={{ marginTop: '15px' }}>
                                <Button
                                  type="primary"
                                  icon={<CloudUploadOutlined />}
                                  onClick={handleImageUpload}
                                  loading={imageUploadLoading}
                                  disabled={selectedImages.length === 0}
                                  size="large"
                                >
                                  {imageUploadLoading ? `Uploading ${selectedImages.length} Images...` : `Upload ${selectedImages.length} Image(s)`}
                                </Button>
                              </div>
                            )}

                            {/* Upload Success Message */}
                            {imagesUploaded && selectedImages.length > 0 && (
                              <div className="upload_success_message" style={{ marginTop: '10px', padding: '10px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '6px' }}>
                                <span style={{ color: '#52c41a', fontWeight: '500' }}>
                                  ✅ {selectedImages.length} image(s) uploaded successfully!
                                </span>
                              </div>
                            )}

                            {imageError && (
                              <div className="error_message">
                                <span className="">Please select at least one image.</span>
                              </div>
                            )}

                            {imageValidationError && (
                              <div className="error_message ant-form-item-explain-error">
                                <span className="">{content?.UploadImage}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Commented out Looking For field - using default text in payload */}
                        {/* <div className="input_holder w_full">
                          <label htmlFor="lookingFor" className="d-block input_label">
                            Looking For <span className="text_red">*</span>
                          </label>
                          <Form.Item
                            name="lookingFor"
                            rules={[
                              { required: true, message: 'Please enter what you are looking for' },
                              { min: 5, message: 'Please enter at least 5 characters' }
                            ]}
                          >
                            <Input
                              className="input_field"
                              placeholder="e.g., Caring life partner, Someone who shares my values"
                              value={formData.lookingFor}
                              onChange={(e) => {
                                const value = e.target.value;
                                handleFieldChange('lookingFor', value);
                                form.setFieldValue('lookingFor', value);
                              }}
                            />
                          </Form.Item>
                        </div> */}

                        <div className="input_holder w_full">
                          <label htmlFor="bio" className=" d-block input_label">
                            Bio <span className="text_red">*</span>
                          </label>
                          <Form.Item
                            name="bio"
                            rules={[
                              { required: true, message: content?.EnterBio },
                              { min: 10, message: 'Bio must be at least 10 characters long' }
                            ]}
                          >
                            <TextArea
                              className="input_field"
                              rows={5}
                              placeholder={placeHolderContent?.Bio}
                              maxLength={fieldLimits.bio}
                              value={formData.bio}
                              onChange={(e) => {
                                const value = e.target.value;
                                handleFieldChange('bio', value);
                                // Update form field value to sync with validation
                                form.setFieldValue('bio', value);
                                // Clear bio validation error when it becomes valid (>= 10 chars)
                                if (value && value.trim().length >= 10) {
                                  try {
                                    form.setFields([{ name: 'bio', errors: [] }]);
                                  } catch (err) {
                                    // no-op
                                  }
                                }
                              }}
                            />
                            <div className="bio_footer">
                              <div className="form_note">
                                <span className={fieldLengths.bio === fieldLimits.bio ? 'limit' : ''}>
                                  {fieldLengths.bio}/{fieldLimits.bio}
                                </span>
                              </div>
                              <Button
                                type="primary"
                                size="small"
                                onClick={regenerateBio}
                                loading={bioRegenerateLoading}
                                disabled={bioRegenerateLoading || !isBioGenerationEnabled()}
                                className="regenerate_bio_btn ml_auto"
                              >
                                {bioRegenerateLoading ? 'Generating...' : 'Regenerate Bio'}
                              </Button>
                            </div>
                          </Form.Item>
                        </div>
                      </div>
                    )
                  },
                  {
                    key: 'additionalDetails',
                    label: 'Additional Information',
                    children: (
                      <div className="accordion_content additional_details">
                        {/* Row 1: Weight and Interests */}
                        <div className="input_holder w_75 multi_select">
                          <label htmlFor="interests" className="d-block input_label">
                            Interest(s)
                          </label>
                          <Form.Item name="interests">
                            <Select
                              className="input_field creation_dropdown interest_dropdown"
                              placeholder={placeHolderContent?.Interests}
                              mode="multiple"
                              onChange={(value) => {
                                handleFieldChange('interests', value);
                                
                                // Merge interests IDs instead of replacing them
                                setInterests(prevInterests => {
                                  // Create a Set to avoid duplicates
                                  const mergedIds = new Set(prevInterests);
                                  value.forEach(id => mergedIds.add(id));
                                  return Array.from(mergedIds);
                                });
                                
                                // Store complete interest objects with code and label
                                const selectedInterests = value.map(interestId => {
                                  const found = interestsList.find(interest => interest.id === interestId || String(interest.id) === interestId);
                                  return found ? { code: found.code, label: found.label } : null;
                                }).filter(Boolean);
                                
                                // Merge with existing interests instead of replacing them
                                setSelectedInterestsData(prevInterests => {
                                  // Create a map of existing interests by code to avoid duplicates
                                  const existingInterestsMap = new Map();
                                  prevInterests.forEach(interest => {
                                    if (interest && interest.code) {
                                      existingInterestsMap.set(interest.code, interest);
                                    }
                                  });
                                  
                                  // Add new interests, avoiding duplicates
                                  selectedInterests.forEach(interest => {
                                    if (interest && interest.code) {
                                      existingInterestsMap.set(interest.code, interest);
                                    }
                                  });
                                  
                                  // Convert map back to array
                                  const mergedInterests = Array.from(existingInterestsMap.values());
                                  return mergedInterests;
                                });
                              }}
                              loading={interestsLoading}
                              onPopupScroll={handleInterestsScroll}
                              showSearch
                              filterOption={false}
                              suffixIcon={<img src={dropdownIcon} alt="" style={{ width: '14px ' , height: '14px' , objectFit: 'contain'}} />}
                              onSearch={(value) => {
                                debouncedSearchInterests(value);
                              }}
                              dropdownRender={(menu) => (
                                <div>
                                  {menu}
                                  {interestsLoadingMore && (
                                    <div style={{
                                      textAlign: 'center',
                                      padding: '8px',
                                      borderTop: '1px solid #f0f0f0',
                                      background: '#fafafa'
                                    }}>
                                      <Spin size="small" /> Loading more interests...
                                    </div>
                                  )}
                                </div>
                              )}
                              notFoundContent={
                                interestsLoading ? (
                                  <div style={{ textAlign: 'center', padding: '8px' }}>
                                    <Spin size="small" /> Loading interests...
                                  </div>
                                ) : (
                                  <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
                                    No interests found
                                  </div>
                                )
                              }
                            >
                              {interestsList && interestsList.length > 0 &&
                                interestsList.map(interest => (
                                  <Option key={interest.id} value={interest.id}>
                                    {interest.label}
                                  </Option>
                                ))
                              }
                            </Select>
                          </Form.Item>
                        </div>
                        <div className="input_holder ">
                          <label htmlFor="weight" className="d-block input_label">
                            Weight
                          </label>
                          <Form.Item name="weight">
                            <Select
                              className="input_field creation_dropdown"
                              placeholder={placeHolderContent?.Weight}
                              onChange={(value, option) => {
                                // Store the complete masterdata object if available
                                const selectedWeight = weightOptions.find(weight => weight.code === value || weight.id === value);
                                if (selectedWeight) {
                                  handleFieldChange('weight', selectedWeight);
                                } else {
                                  handleFieldChange('weight', value);
                                }
                              }}
                              loading={weightLoading}
                              onPopupScroll={handleWeightScroll}
                              showSearch
                              filterOption={false}
                              suffixIcon={<img src={dropdownIcon} alt="" style={{ width: '14px ' , height: '14px' , objectFit: 'contain'}} />}
                              onSearch={(value) => {
                                debouncedSearchWeights(value);
                              }}
                              dropdownRender={(menu) => (
                                <div>
                                  {menu}
                                  {weightLoadingMore && (
                                    <div style={{
                                      textAlign: 'center',
                                      padding: '8px',
                                      borderTop: '1px solid #f0f0f0',
                                      background: '#fafafa'
                                    }}>
                                      <Spin size="small" /> Loading more weights...
                                    </div>
                                  )}
                                </div>
                              )}
                              notFoundContent={
                                weightLoading ? (
                                  <div style={{ textAlign: 'center', padding: '8px' }}>
                                    <Spin size="small" /> Loading weights...
                                  </div>
                                ) : (
                                  <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
                                    No weights found
                                  </div>
                                )
                              }
                            >
                              {weightOptions && weightOptions.length > 0 &&
                                weightOptions.map(weight => (
                                  <Option key={weight.id} value={weight.code || weight.id}>
                                    {weight.label}
                                  </Option>
                                ))
                              }
                            </Select>
                          </Form.Item>
                        </div>

                        {/* Row 2: Birth Star, Birth Time, Family Status, Dosham/Manglik */}
                        <div className="input_holder">
                          <label htmlFor="birthStar" className="d-block input_label">
                            Birth Star
                          </label>
                          <Form.Item name="birthStar">
                            <Select
                              className="input_field creation_dropdown"
                              placeholder={placeHolderContent?.BirthStar}
                              onChange={(value) => handleFieldChange('birthStar', value)}
                              loading={birthStarsLoading}
                              onPopupScroll={handleBirthStarsScroll}
                              showSearch
                              filterOption={false}
                              suffixIcon={<img src={dropdownIcon} alt="" style={{ width: '14px ' , height: '14px' , objectFit: 'contain'}} />}
                              onSearch={(value) => {
                                debouncedSearchBirthStars(value);
                              }}
                              dropdownRender={(menu) => (
                                <div>
                                  {menu}
                                  {birthStarsLoadingMore && (
                                    <div style={{
                                      textAlign: 'center',
                                      padding: '8px',
                                      borderTop: '1px solid #f0f0f0',
                                      background: '#fafafa'
                                    }}>
                                      <Spin size="small" /> Loading more birth stars...
                                    </div>
                                  )}
                                </div>
                              )}
                              notFoundContent={
                                birthStarsLoading ? (
                                  <div style={{ textAlign: 'center', padding: '8px' }}>
                                    <Spin size="small" /> Loading birth stars...
                                  </div>
                                ) : (
                                  <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
                                    No birth stars found
                                  </div>
                                )
                              }
                            >
                              {birthStars && birthStars.length > 0 &&
                                birthStars.map(star => (
                                  <Option key={star.id} value={star.code || star.label}>
                                    {star.label}
                                  </Option>
                                ))
                              }
                            </Select>
                          </Form.Item>
                        </div>

                        <div className="input_holder">
                          <label htmlFor="birthTime" className="d-block input_label">
                            Birth Time
                          </label>
                          <Form.Item
                            name="birthTime"
                            validateStatus=""
                            help=""
                          >
                            <CustomTimePicker
                              className="input_field"
                              placeholder={placeHolderContent?.BirthTime || "Select time"}
                              value={form.getFieldValue('birthTime') || formData.birthTime || ''}
                              onChange={(timeString) => {
                                // Ensure we have a clean string value
                                const cleanTimeString = timeString || '';

                                // Update form field first, then form data
                                form.setFieldValue('birthTime', cleanTimeString);
                                handleFieldChange('birthTime', cleanTimeString);
                              }}
                              format="12"
                              clearIcon={true}
                              clockIcon={true}
                            />
                          </Form.Item>
                        </div>

                        <div className="input_holder">
                          <label htmlFor="familyStatus" className="d-block input_label">
                            Family Status
                          </label>
                          <Form.Item name="familyStatus">
                            <Select
                              className="input_field creation_dropdown"
                              placeholder={placeHolderContent?.FamilyStatus}
                              onChange={(value) => handleFieldChange('familyStatus', value)}
                              loading={familyTypesLoading}
                              onPopupScroll={handleFamilyTypesScroll}
                              showSearch
                              filterOption={false}
                              suffixIcon={<img src={dropdownIcon} alt="" style={{ width: '14px ' , height: '14px' , objectFit: 'contain'}} />}
                              onSearch={(value) => {
                                debouncedSearchFamilyTypes(value);
                              }}
                              dropdownRender={(menu) => (
                                <div>
                                  {menu}
                                  {familyTypesLoadingMore && (
                                    <div style={{
                                      textAlign: 'center',
                                      padding: '8px',
                                      borderTop: '1px solid #f0f0f0',
                                      background: '#fafafa'
                                    }}>
                                      <Spin size="small" /> Loading more family types...
                                    </div>
                                  )}
                                </div>
                              )}
                              notFoundContent={
                                familyTypesLoading ? (
                                  <div style={{ textAlign: 'center', padding: '8px' }}>
                                    <Spin size="small" /> Loading family types...
                                  </div>
                                ) : (
                                  <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
                                    No family types found
                                  </div>
                                )
                              }
                            >
                              {familyTypes && familyTypes.length > 0 &&
                                familyTypes.map(familyType => (
                                  <Option key={familyType.id} value={familyType.code || familyType.label}>
                                    {familyType.label}
                                  </Option>
                                ))
                              }
                            </Select>
                          </Form.Item>
                        </div>

                        <div className="input_holder">
                          <label htmlFor="dosham" className="d-block input_label">
                            Dosham/Manglik
                          </label>
                          <Form.Item name="dosham">
                            <Select
                              className="input_field creation_dropdown"
                              placeholder={placeHolderContent?.Dosham}
                              onChange={(value) => handleFieldChange('dosham', value)}
                              loading={manglikLoading}
                              onPopupScroll={handleManglikScroll}
                              showSearch
                              filterOption={false}
                              suffixIcon={<img src={dropdownIcon} alt="" style={{ width: '14px ' , height: '14px' , objectFit: 'contain'}} />}
                              onSearch={(value) => {
                                debouncedSearchManglikLists(value);
                              }}
                              dropdownRender={(menu) => (
                                <div>
                                  {menu}
                                  {manglikLoadingMore && (
                                    <div style={{
                                      textAlign: 'center',
                                      padding: '8px',
                                      borderTop: '1px solid #f0f0f0',
                                      background: '#fafafa'
                                    }}>
                                      <Spin size="small" /> Loading more manglik options...
                                    </div>
                                  )}
                                </div>
                              )}
                              notFoundContent={
                                manglikLoading ? (
                                  <div style={{ textAlign: 'center', padding: '8px' }}>
                                    <Spin size="small" /> Loading manglik options...
                                  </div>
                                ) : (
                                  <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
                                    No manglik options found
                                  </div>
                                )
                              }
                            >
                              {manglikOptions && manglikOptions.length > 0 &&
                                manglikOptions.map(manglik => (
                                  <Option key={manglik.id} value={manglik.code || manglik.id}>
                                    {manglik.label}
                                  </Option>
                                ))
                              }
                            </Select>
                          </Form.Item>
                        </div>

                        {/* Row 2: Birth Place (full width) */}
                        <div className="input_holder w_full">
                          <label htmlFor="birthPlace" className="d-block input_label">
                            Birth Place
                          </label>
                          <Form.Item name="birthPlace">
                            <LocationSelection
                              apiUrl={API_ENDPOINTS.LOCATION.PLACES}
                              placeholder={placeHolderContent?.BirthPlace}
                              selectedLocation={handleBirthPlaceSelection}
                              value={formData.birthPlace}
                              externalLoading={
                                languagesLoading || religionsLoading || castesLoading || heightsLoading ||
                                jobsLoading || educationsLoading || birthStarsLoading || weightLoading ||
                                interestsLoading || foodLoading || drinkingLoading || smokingLoading ||
                                ideologyLoading || manglikLoading
                              }
                              errorHandle={(hasError) => {
                              }}
                              disableStatus={false}
                              debug={true}
                            />
                          </Form.Item>
                        </div>

                        {/* Row 3: Known Languages (full width) */}
                        <div className="input_holder w_full multi_select known_languages_dropdown">
                          <label htmlFor="knownLanguages" className="d-block input_label">
                            Known Languages
                          </label>
                          <Form.Item name="knownLanguages">
                            <Select
                              mode="multiple"
                              creation_dropdown className="input_field"
                              placeholder={placeHolderContent?.KnownLanguages}
                              onChange={(value) => {
                                handleFieldChange('knownLanguages', value);
                                
                                // Store complete language objects with code and label
                                const selectedLanguages = value.map(languageId => {
                                  const found = languages.find(language => language.id === languageId || String(language.id) === languageId);
                                  return found ? { code: found.code, label: found.label } : null;
                                }).filter(Boolean);
                                
                                setSelectedLanguagesData(selectedLanguages);
                              }}
                              loading={languagesLoading}
                              onPopupScroll={handleLanguagesScroll}
                              showSearch
                              filterOption={false}
                              suffixIcon={<img src={dropdownIcon} alt="" style={{ width: '14px ' , height: '14px' , objectFit: 'contain'}} />}
                              onSearch={(value) => {
                                debouncedSearchLanguages(value);
                              }}
                              dropdownRender={(menu) => (
                                <div>
                                  {menu}
                                  {languagesLoadingMore && (
                                    <div style={{
                                      textAlign: 'center',
                                      padding: '8px',
                                      borderTop: '1px solid #f0f0f0',
                                      background: '#fafafa'
                                    }}>
                                      <Spin size="small" /> Loading more languages...
                                    </div>
                                  )}
                                </div>
                              )}
                              notFoundContent={languagesLoading ? <Spin size="small" /> : null}
                              maxTagCount="responsive"
                            >
                              {languages && languages.length > 0 &&
                                languages.map(language => (
                                  <Option key={language.id} value={language.id}>
                                    {language.label || language.name}
                                  </Option>
                                ))
                              }
                            </Select>
                          </Form.Item>
                        </div>

                        {/* Row 4: Father's Name, Father's Occupation, Family Name */}
                        <div className="input_holder w_one_third">
                          <label htmlFor="fatherName" className="d-block input_label">
                            Father's Name
                          </label>
                          <Form.Item name="fatherName">
                            <Input
                              className="input_field"
                              placeholder={placeHolderContent?.FatherName}
                              onChange={(e) => handleFieldChange('fatherName', e.target.value)}
                            />
                          </Form.Item>
                        </div>

                        <div className="input_holder w_one_third">
                          <label htmlFor="fatherOccupation" className="d-block input_label">
                            Father's Occupation
                          </label>
                          <Form.Item name="fatherOccupation">
                            <Input
                              className="input_field"
                              placeholder={placeHolderContent?.FatherOccupation}
                              onChange={(e) => handleFieldChange('fatherOccupation', e.target.value)}
                            />
                          </Form.Item>
                        </div>

                        <div className="input_holder w_one_third">
                          <label htmlFor="familyName" className="d-block input_label">
                            Family Name
                          </label>
                          <Form.Item name="familyName">
                            <Input
                              className="input_field"
                              placeholder={placeHolderContent?.FamilyName}
                              onChange={(e) => handleFieldChange('familyName', e.target.value)}
                            />
                          </Form.Item>
                        </div>
                        {/* Row 5: Mother's Name, Mother's Occupation, Siblings */}
                        <div className="input_holder w_one_third mother_name_input">
                          <label htmlFor="motherName" className="d-block input_label">
                            Mother's Name
                          </label>
                          <Form.Item name="motherName">
                            <Input
                              className="input_field"
                              placeholder={placeHolderContent?.MotherName}
                              onChange={(e) => handleFieldChange('motherName', e.target.value)}
                            />
                          </Form.Item>
                        </div>

                        <div className="input_holder w_one_third mother_occupation_input">
                          <label htmlFor="motherOccupation" className="d-block input_label">
                            Mother's Occupation
                          </label>
                          <Form.Item name="motherOccupation">
                            <Input
                              className="input_field"
                              placeholder={placeHolderContent?.MotherOccupation}
                              onChange={(e) => handleFieldChange('motherOccupation', e.target.value)}
                            />
                          </Form.Item>
                        </div>

                        <div className="input_holder w_one_third siblings_selector">
                          <label className="d-block input_label text_red">
                            Siblings
                          </label>
                          <div className="siblings_section_compact">
                            <div className="siblings_count_inline">
                              <div className="sibling_count_item">
                                <span className="sibling_label">Brother(s):</span>
                                <Form.Item name="brothersCount" style={{ margin: 0, display: 'inline-block' }}>
                                  <Select
                                    className=" siblings_c creation_dropdownount_select"
                                    placeholder="0"
                                    style={{ width: '60px' }}
                                    onChange={(value) => handleFieldChange('brothersCount', value)}
                                    suffixIcon={<img src={dropdownIcon} alt="" style={{ width: '14px ' , height: '14px' , objectFit: 'contain'}} />}
                                  >
                                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                                      <Option key={num} value={num}>{num}</Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </div>
                              <div className="sibling_count_item">
                                <span className="sibling_label">Sister(s):</span>
                                <Form.Item name="sistersCount" style={{ margin: 0, display: 'inline-block' }}>
                                  <Select
                                    className=" siblings_c creation_dropdownount_select"
                                    placeholder="0"
                                    style={{ width: '60px' }}
                                    onChange={(value) => handleFieldChange('sistersCount', value)}
                                    suffixIcon={<img src={dropdownIcon} alt="" style={{ width: '14px ' , height: '14px' , objectFit: 'contain'}} />}
                                  >
                                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                                      <Option key={num} value={num}>{num}</Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </div>
                            </div>
                          </div>
                        </div>



                        {/* Row 6: Family Location (full width) */}
                        {/* IMPORTANT: Family Location is stored and sent as a SIMPLE STRING only,
                            unlike the main Location field which sends a complete location object.
                            The LocationSelection component is used for UX convenience only. */}
                        <div className="input_holder w_full">
                          <label htmlFor="familyLocation" className="d-block input_label">
                            Family Location
                          </label>
                          <Form.Item name="familyLocation">
                            <LocationSelection
                              apiUrl={API_ENDPOINTS.LOCATION.PLACES}
                              placeholder={placeHolderContent?.FamilyLocation}
                              selectedLocation={handleFamilyLocationSelection}
                              value={formData.familyLocation}
                              externalLoading={
                                languagesLoading || religionsLoading || castesLoading || heightsLoading ||
                                jobsLoading || educationsLoading || birthStarsLoading || weightLoading ||
                                interestsLoading || foodLoading || drinkingLoading || smokingLoading ||
                                ideologyLoading || manglikLoading
                              }
                              errorHandle={(hasError) => {
                              }}
                              disableStatus={false}
                              debug={true}
                            />
                          </Form.Item>
                        </div>

                        {/* Row 7: Family Description (full width) */}
                        <div className="input_holder w_full">
                          <label htmlFor="familyDescription" className="d-block input_label">
                            Family Description
                          </label>
                          <Form.Item name="familyDescription">
                            <TextArea
                              className="input_field"
                              rows={4}
                              placeholder={placeHolderContent?.FamilyDescription}
                              maxLength={350}
                              onChange={(e) => handleFieldChange('familyDescription', e.target.value)}
                            />
                          </Form.Item>
                        </div>

                        {/* Row 8: Drinking, Ideologies & Beliefs, Food, Smoking */}
                        <div className="input_holder">
                          <label htmlFor="drinking" className="d-block input_label">
                            Drinking
                          </label>
                          <Form.Item name="drinking">
                            <Select
                              className="input_field creation_dropdown"
                              placeholder={placeHolderContent?.Drinking}
                              onChange={(value) => {
                                // Persist selected code; UI shows label via options
                                handleFieldChange('drinking', value);
                              }}
                              loading={drinkingLoading}
                              onPopupScroll={handleDrinkingScroll}
                              showSearch
                              filterOption={false}
                              suffixIcon={<img src={dropdownIcon} alt="" style={{ width: '14px ' , height: '14px' , objectFit: 'contain'}} />}
                              onSearch={(value) => {
                                debouncedSearchDrinking(value);
                              }}
                              dropdownRender={(menu) => (
                                <div>
                                  {menu}
                                  {drinkingLoadingMore && (
                                    <div style={{
                                      textAlign: 'center',
                                      padding: '8px',
                                      borderTop: '1px solid #f0f0f0',
                                      background: '#fafafa'
                                    }}>
                                      <Spin size="small" /> Loading more drinking options...
                                    </div>
                                  )}
                                </div>
                              )}
                              notFoundContent={
                                drinkingLoading ? (
                                  <div style={{ textAlign: 'center', padding: '8px' }}>
                                    <Spin size="small" /> Loading drinking options...
                                  </div>
                                ) : (
                                  <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
                                    No drinking options found
                                  </div>
                                )
                              }
                            >
                              {drinkingOptions && drinkingOptions.length > 0 &&
                                extractSubDivisionOptions(drinkingOptions).map(option => (
                                  <Option key={option.id} value={option.value}>
                                    {option.label}
                                  </Option>
                                ))
                              }
                            </Select>
                          </Form.Item>
                        </div>

                        <div className="input_holder">
                          <label htmlFor="ideologies" className="d-block input_label">
                            Ideologies & Beliefs
                          </label>
                          <Form.Item name="ideologies">
                            <Select
                              className="input_field creation_dropdown"
                              placeholder={placeHolderContent?.Ideologies}
                              onChange={(value) => {
                                handleFieldChange('ideologies', value);
                              }}
                              loading={ideologyLoading}
                              onPopupScroll={handleIdeologyScroll}
                              showSearch
                              filterOption={false}
                              suffixIcon={<img src={dropdownIcon} alt="" style={{ width: '14px ' , height: '14px' , objectFit: 'contain'}} />}
                              onSearch={(value) => {
                                debouncedSearchIdeologies(value);
                              }}
                              dropdownRender={(menu) => (
                                <div>
                                  {menu}
                                  {ideologyLoadingMore && (
                                    <div style={{
                                      textAlign: 'center',
                                      padding: '8px',
                                      borderTop: '1px solid #f0f0f0',
                                      background: '#fafafa'
                                    }}>
                                      <Spin size="small" /> Loading more ideologies...
                                    </div>
                                  )}
                                </div>
                              )}
                              notFoundContent={
                                ideologyLoading ? (
                                  <div style={{ textAlign: 'center', padding: '8px' }}>
                                    <Spin size="small" /> Loading ideologies...
                                  </div>
                                ) : (
                                  <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
                                    No ideologies found
                                  </div>
                                )
                              }
                            >
                              {ideologyOptions && ideologyOptions.length > 0 &&
                                extractSubDivisionOptions(ideologyOptions).map(option => (
                                  <Option key={option.id} value={option.value}>
                                    {option.label}
                                  </Option>
                                ))
                              }
                            </Select>
                          </Form.Item>
                        </div>

                        <div className="input_holder">
                          <label htmlFor="food" className="d-block input_label">
                            Food
                          </label>
                          <Form.Item name="food">
                            <Select
                              className="input_field creation_dropdown"
                              placeholder={placeHolderContent?.Food}
                              onChange={(value) => {
                                handleFieldChange('food', value);
                              }}
                              loading={foodLoading}
                              onPopupScroll={handleFoodScroll}
                              showSearch
                              filterOption={false}
                              suffixIcon={<img src={dropdownIcon} alt="" style={{ width: '14px ' , height: '14px' , objectFit: 'contain'}} />}
                              onSearch={(value) => {
                                debouncedSearchFood(value);
                              }}
                              dropdownRender={(menu) => (
                                <div>
                                  {menu}
                                  {foodLoadingMore && (
                                    <div style={{
                                      textAlign: 'center',
                                      padding: '8px',
                                      borderTop: '1px solid #f0f0f0',
                                      background: '#fafafa'
                                    }}>
                                      <Spin size="small" /> Loading more food options...
                                    </div>
                                  )}
                                </div>
                              )}
                              notFoundContent={
                                foodLoading ? (
                                  <div style={{ textAlign: 'center', padding: '8px' }}>
                                    <Spin size="small" /> Loading food options...
                                  </div>
                                ) : (
                                  <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
                                    No food options found
                                  </div>
                                )
                              }
                            >
                              {foodOptions && foodOptions.length > 0 &&
                                extractSubDivisionOptions(foodOptions).map(option => (
                                  <Option key={option.id} value={option.value}>
                                    {option.label}
                                  </Option>
                                ))
                              }
                            </Select>
                          </Form.Item>
                        </div>

                        <div className="input_holder">
                          <label htmlFor="smoking" className="d-block input_label">
                            Smoking
                          </label>
                          <Form.Item name="smoking">
                            <Select
                              className="input_field creation_dropdown"
                              placeholder={placeHolderContent?.Smoking}
                              onChange={(value) => {
                                handleFieldChange('smoking', value);
                              }}
                              loading={smokingLoading}
                              onPopupScroll={handleSmokingScroll}
                              showSearch
                              filterOption={false}
                              onSearch={(value) => {
                                debouncedSearchSmoking(value);
                              }}
                              dropdownRender={(menu) => (
                                <div>
                                  {menu}
                                  {smokingLoadingMore && (
                                    <div style={{
                                      textAlign: 'center',
                                      padding: '8px',
                                      borderTop: '1px solid #f0f0f0',
                                      background: '#fafafa'
                                    }}>
                                      <Spin size="small" /> Loading more smoking options...
                                    </div>
                                  )}
                                </div>
                              )}
                              notFoundContent={
                                smokingLoading ? (
                                  <div style={{ textAlign: 'center', padding: '8px' }}>
                                    <Spin size="small" /> Loading smoking options...
                                  </div>
                                ) : (
                                  <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
                                    No smoking options found
                                  </div>
                                )
                              }
                            >
                              {smokingOptions && smokingOptions.length > 0 &&
                                extractSubDivisionOptions(smokingOptions).map(option => (
                                  <Option key={option.id} value={option.value}>
                                    {option.label}
                                  </Option>
                                ))
                              }
                            </Select>
                          </Form.Item>
                        </div>

                        {/* Upload Horoscope Section */}
                        <div className="image_upload_section  input_holder w_full">
                          <label className="d-block input_label">
                            Upload Horoscope (Max: 2 documents)
                          </label>
                          <div className="image_selector_wrapper ">
                            <ImageSelector
                              value={selectedHoroscopeImages}
                              onChange={handleHoroscopeImageChange}
                              maxImages={2}
                              maxFileSize={5 * 1024 * 1024}
                              acceptedTypes={['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf']}
                              aspectRatio={null}
                              minDimensions={{ width: 150, height: 150 }}
                            />

                            {/* Upload Button */}
                            {showHoroscopeUploadButton && (
                              <div className="upload_button_container" style={{ marginTop: '15px' }}>
                                <Button
                                  type="primary"
                                  icon={<CloudUploadOutlined />}
                                  onClick={handleHoroscopeImageUpload}
                                  loading={horoscopeUploadLoading}
                                  disabled={selectedHoroscopeImages.length === 0}
                                  size="large"
                                >
                                  {horoscopeUploadLoading ? `Uploading ${selectedHoroscopeImages.length} Document(s)...` : `Upload ${selectedHoroscopeImages.length} Document(s) (Max: 2)`}
                                </Button>
                              </div>
                            )}

                            {/* Upload Success Message */}
                            {horoscopeImagesUploaded && uploadedHoroscopeUrls.length > 0 && (
                              <div className="upload_success_message" style={{ marginTop: '10px', padding: '10px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '6px' }}>
                                <span style={{ color: '#52c41a', fontWeight: '500' }}>
                                  ✅ {uploadedHoroscopeUrls.length} horoscope document(s) uploaded successfully!
                                </span>
                              </div>
                            )}

                            {horoscopeImageError && (
                              <div className="error_message">
                                <span className="">Please select at least one horoscope document.</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  }
                ]}
              />

              {/* Submit Button */}
              <div className="form_submit profile_creation_submit">
                <Form.Item>
                  <Button
                    type="default"
                    className="back_btn"
                    style={{ marginRight: '10px' }}
                    onClick={() => navigate('/dashboard')}
                  >
                    Back
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={submissionLoading}
                    disabled={!canSubmitForm}
                    className="submit_btn text_md2"
                    align="center"
                    onClick={() => {
                      // Clear validation errors for properly filled fields first
                      clearFormValidationErrors();
                      
                      // Trigger image validation first
                      const imageValidationOk = triggerImageValidation();
                      
                      // Ensure we surface what is missing if basic details are incomplete
                      const basicOk = validateBasicDetails();
                      if (!basicOk) {
                        // Trigger ant form validation to collect missing fields
                        form
                          .validateFields()
                          .catch(() => {
                            // handled in onFinishFailed
                          });
                        return;
                      }
                      
                      // If image validation failed, don't proceed
                      if (!imageValidationOk) {
                        return;
                      }
                      
                      // Check for images that need to be uploaded
                      const hasNewImages = selectedImages.some(img => img.file && img.file instanceof File);
                      const hasNewHoroscopeImages = selectedHoroscopeImages.some(img => img.file && img.file instanceof File);

                      if (hasNewImages && !imagesUploaded) {
                        message.warning('Please upload the selected images before continuing.');
                        return;
                      }
                      if (hasNewHoroscopeImages && !horoscopeImagesUploaded) {
                        message.warning('Please upload the selected horoscope documents before continuing.');
                        return;
                      }
                    }}
                  >
                    {submissionLoading
                      ? (isEditMode ? 'Updating Profile...' : 'Creating Profile...')
                      : (isEditMode ? 'Update' : 'Next')
                    }
                  </Button>
                  {/* <Button
                    type="primary"
                    className="preview_btn"
                    style={{ marginLeft: '10px' }}
                    onClick={showPreviewModal}
                  >
                    Show Preview
                  </Button> */}
                  {!canSubmitForm && (
                    <div style={{ marginTop: '8px', color: '#ff4d4f', fontSize: '12px' }}>
                      {(() => {
                        const hasNewImages = selectedImages.some(img => img.file && img.file instanceof File);
                        const hasNewHoroscopeImages = selectedHoroscopeImages.some(img => img.file && img.file instanceof File);

                        if (hasNewImages && !imagesUploaded) {
                          return 'Please upload the selected images before submitting';
                        }
                        if (hasNewHoroscopeImages && !horoscopeImagesUploaded) {
                          return 'Please upload the selected horoscope documents before submitting';
                        }
                        if ((hasNewImages && !imagesUploaded) && (hasNewHoroscopeImages && !horoscopeImagesUploaded)) {
                          return 'Please upload the selected images and horoscope documents before submitting';
                        }
                        return 'Please fill all required basic details and upload at least one image';
                      })()}
                    </div>
                  )}
                </Form.Item>
              </div>
            </Form>
          </div>
        </Card>
      </div>

      {/* Preview Modal */}
      <Modal
        afterClose={() => (document.body.style.overflow = "initial")}
        className="sm_modal new_add_modal"
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div className="mobile_preview_top">
              <h2 className="text-center text_red text_lg">Profile Preview</h2>
              {/* <p
                style={{
                  fontWeight: "400",
                  textAlign: "center",
                  fontSize: "14px",
                }}
                className="mb-0"
              >
                See how your Profile looks in the Lumi App.
              </p> */}
            </div>
            <Button
              type="text"
              onClick={handlePreviewCancel}
              className="custom-close-button"
            >
              Close
            </Button>
          </div>
        }
        open={isPreviewModalOpen}
        onCancel={handlePreviewCancel}
        footer={null}
        closeIcon={null}
      >
        {/* Tab Navigation */}
        <div className="preview-tabs">
          <button
            className={`tab-button ${activePreviewTab === 'listing' ? 'active' : ''}`}
            onClick={() => setActivePreviewTab('listing')}
          >
            Listing
          </button>
          <button
            className={`tab-button ${activePreviewTab === 'profile-details' ? 'active' : ''}`}
            onClick={() => setActivePreviewTab('profile-details')}
          >
            Profile Details
          </button>
        </div>

        {/* Tab Content */}
        <div className="preview-content">
          {activePreviewTab === 'listing' ? (
            <Mobile action="enquiry" previewTab={'listing'}>
              <ListingView data={(() => {
                const dataToUse = mockListingData.length > 0 ? mockListingData : (mobListingData?.data || []);
                const currentUserName = formData.fullName || form.getFieldValue('fullName');
                
                // Filter out profiles with the same name as current user to prevent duplicates
                return dataToUse.filter(profile => profile.name !== currentUserName);
              })()}>
                <Advertisement
                  action="enquiry"
                  poster={selectedImages.length > 0 ? selectedImages[0]?.url : ''}
                  icon="enquiry"
                  profileData={transformFormDataForAdvertisement({
                    ...formData,
                    ...form.getFieldsValue()
                  })}
                />
              </ListingView>
            </Mobile>
          ) : (
            <Mobile action="enquiry" previewTab={'profile-details'}>
              <ProfileDetails
                className={'right_aligned'}
                profileData={{
                  name: formData.fullName,
                  bio: formData.bio,
                  lumi_id: formData.lumiId,
                  age: formData.age,
                  dob: formData.dob ? dayjs(formData.dob).format('DD-MM-YYYY') : undefined,
                  height: formData.height,
                  marital_status: formData.maritalStatus,
                  religion: formData.religion,
                  caste: formData.caste,
                  zodiac_sign: formData.birthStar,
                  city: selectedLocation ? 
                    (selectedLocation.city?.label || selectedLocation.city || selectedLocation.displayPlace || selectedLocation.displayName || selectedLocation.name) : 
                    (formData.location || 'Not specified'),
                  state: selectedLocation ? 
                    (selectedLocation.state?.label || selectedLocation.state) : 
                    'Not specified',
                  // Also pass the raw location string for fallback
                  location: formData.location || 'Not specified',
                  locationData: selectedLocation,
                  about_me: formData.aboutMe,
                  education: formData.education,
                  job: formData.job,
                  company: formData.company,
                  country: formData.country,
                  profile_images: selectedImages.length > 0 ? selectedImages.map(img => ({ original: img.url })) : [],
                  compatibility_score: undefined
                }}
              />
            </Mobile>
          )}
        </div>
      </Modal>

      {/* Preview Error Modal */}
      <Modal
        className="sm_modal new_add_modal work_modal tex-center mobile_preview_error"
        open={previewErrorModalOpen}
        footer={null}
        onCancel={handlePreviewCancel}
      >
        <p className="text-center text_red mb-0">
          Please fill all required basic details before previewing your profile.
        </p>
      </Modal>
    </>
  );
};

export default ProfileCreation;